import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LogOut, Users, DollarSign, TrendingUp, UserCheck, UserX, Search, Filter, Percent, Settings, Newspaper, ShieldCheck, Plus, UserPlus, RefreshCw, CheckSquare, Square, Trash2, Building2, Eye, EyeOff, Download, AlertTriangle, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown, Clock, Mail, CreditCard, FileImage, CheckCircle, XCircle, Edit, Key, Check, Info, Award, Calendar, Zap, Loader2, PackageSearch, ImageUp, Link2 as LinkIcon, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import PayoutManagement from '@/components/PayoutManagement';
import AccessCodeManager from '@/components/AccessCodeManager';
import BlogAdmin from '@/components/BlogAdmin';
import AuthorVerificationAdmin from '@/components/AuthorVerificationAdmin';
import AdminRoyalties from '@/components/AdminRoyalties';
import RoyaltyReconciliation from '@/components/RoyaltyReconciliation';
import PlaySyncPanel from '@/components/PlaySyncPanel';
import ImgToolPanel from '@/components/ImgToolPanel';
import ViewAsUserModal from '@/components/ViewAsUserModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper para formatear moneda con comas
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0.00';
  return '$' + Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const AdminPanel = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Ordenamiento
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Automatización
  const [expiringMembers, setExpiringMembers] = useState([]);
  const [automationLoading, setAutomationLoading] = useState(false);
  const [hasAdminAccess, setHasAdminAccess] = useState(null);
  const [accessCheckLoading, setAccessCheckLoading] = useState(true);
  
  // Delete User
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePin, setDeletePin] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // MercadoPago Payment Verification
  const [mpPaymentId, setMpPaymentId] = useState('');
  const [mpVerificationResult, setMpVerificationResult] = useState(null);
  const [verifyingMpPayment, setVerifyingMpPayment] = useState(false);
  
  // Transaction Update Modal
  const [showUpdateTxModal, setShowUpdateTxModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [newTxStatus, setNewTxStatus] = useState('paid');
  const [txMpPaymentId, setTxMpPaymentId] = useState('');
  const [txNotes, setTxNotes] = useState('');
  const [updatingTx, setUpdatingTx] = useState(false);
  
  // Publica.la Sync
  const [syncingUserId, setSyncingUserId] = useState(null);
  
  // View As User
  const [viewAsUserId, setViewAsUserId] = useState(null);
  
  // Revenue Breakdown
  const [revenueBreakdown, setRevenueBreakdown] = useState(null);
  const [showRevenueDetail, setShowRevenueDetail] = useState(false);
  
  // ViubuxPlay Stats
  const [viubuxplayStats, setViubuxplayStats] = useState(null);
  const [loadingViubuxplayStats, setLoadingViubuxplayStats] = useState(false);
  const [showViubuxplayStats, setShowViubuxplayStats] = useState(false);
  
  // Create User Modal
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    full_name: '',
    username: '',
    user_type: 'consumer'
  });
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Bulk Selection
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState([]);
  const [selectedCommissionIds, setSelectedCommissionIds] = useState([]);
  
  // Discount Settings
  const [discountSettings, setDiscountSettings] = useState(null);
  const [discountStats, setDiscountStats] = useState(null);
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [newDiscountPercent, setNewDiscountPercent] = useState(20);
  const [newDiscountMonths, setNewDiscountMonths] = useState(3);
  const [discountActive, setDiscountActive] = useState(true);
  const [savingDiscount, setSavingDiscount] = useState(false);
  
  // Assign Referral
  const [showAssignReferralModal, setShowAssignReferralModal] = useState(false);
  const [userToAssignReferral, setUserToAssignReferral] = useState(null);
  const [referralCodeToAssign, setReferralCodeToAssign] = useState('');
  const [assigningReferral, setAssigningReferral] = useState(false);
  const [usersWithoutReferral, setUsersWithoutReferral] = useState([]);

  // Edit Author Profile (Admin)
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [profileForm, setProfileForm] = useState({ profile_photo: '', cover_image: '', bio: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingField, setUploadingField] = useState('');
  const [workForm, setWorkForm] = useState({ title: '', cover_image: '', public_url: '', formats: [] });
  const [registeringWork, setRegisteringWork] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTargetEmail, setMergeTargetEmail] = useState('');
  const [mergeMode, setMergeMode] = useState('editor');
  const [mergeLoading, setMergeLoading] = useState(false);
  
  // Subsidy Type
  const [subsidyType, setSubsidyType] = useState('30_days'); // "30_days" or "indefinite"
  const [subsidyNotes, setSubsidyNotes] = useState('');
  
  // Bank Transfer Proofs
  const [bankProofs, setBankProofs] = useState([]);
  const [loadingProofs, setLoadingProofs] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);
  const [showProofModal, setShowProofModal] = useState(false);
  const [processingProof, setProcessingProof] = useState(false);
  
  // Reset Finances Modal
  const [showResetFinancesModal, setShowResetFinancesModal] = useState(false);
  const [resetFinancesPin, setResetFinancesPin] = useState('');
  const [resetFinancesLoading, setResetFinancesLoading] = useState(false);
  const [resetFinancesOptions, setResetFinancesOptions] = useState({
    reset_transactions: true,
    reset_commissions: true,
    reset_user_balances: true,
    reset_memberships: true,
    reset_royalty_reports: true
  });
  const [resetFinancesConfirmText, setResetFinancesConfirmText] = useState('');
  
  // Free Trial Settings
  const [trialSettings, setTrialSettings] = useState({
    is_active: false,
    trial_days: 14,
    grant_publicala_access: true,
    grant_viubuxplay_access: true
  });
  const [loadingTrialSettings, setLoadingTrialSettings] = useState(false);
  const [savingTrialSettings, setSavingTrialSettings] = useState(false);
  
  // Simulator Metrics Visibility
  const [simulatorMetricsVisible, setSimulatorMetricsVisible] = useState(true);
  const [loadingSimulatorSettings, setLoadingSimulatorSettings] = useState(false);
  const [savingSimulatorSettings, setSavingSimulatorSettings] = useState(false);
  
  // Paid but Inactive Detection
  const [paidButInactive, setPaidButInactive] = useState([]);
  const [loadingPaidInactive, setLoadingPaidInactive] = useState(false);
  const [fixingUser, setFixingUser] = useState(null);
  const [fixingAll, setFixingAll] = useState(false);
  
  // Finance Backups
  const [financeBackups, setFinanceBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [showBackupsSection, setShowBackupsSection] = useState(false);
  
  // Fix Annual Subscriptions
  const [annualIssues, setAnnualIssues] = useState([]);
  const [loadingAnnualIssues, setLoadingAnnualIssues] = useState(false);
  const [fixingAnnual, setFixingAnnual] = useState(null);
  const [showFixAnnualModal, setShowFixAnnualModal] = useState(false);
  const [manualFixEmail, setManualFixEmail] = useState('');
  const [manualFixAmount, setManualFixAmount] = useState('');
  
  // Run Expired Check
  const [runningExpiredCheck, setRunningExpiredCheck] = useState(false);
  const [expiredCheckResult, setExpiredCheckResult] = useState(null);
  
  // Commissions Summary
  const [commissionsSummary, setCommissionsSummary] = useState(null);
  const [loadingCommissions, setLoadingCommissions] = useState(false);
  const [commissionsMonth, setCommissionsMonth] = useState(''); // empty = all time
  
  // Exchange Rate Configuration
  const [exchangeRate, setExchangeRate] = useState(17.0);
  const [exchangeRateUpdatedAt, setExchangeRateUpdatedAt] = useState(null);
  const [newExchangeRate, setNewExchangeRate] = useState('');
  const [editingExchangeRate, setEditingExchangeRate] = useState(false);
  const [savingExchangeRate, setSavingExchangeRate] = useState(false);
  
  // Publica.la Sync
  const [publicalaStats, setPublicalaStats] = useState(null);
  const [publicalaEvents, setPublicalaEvents] = useState([]);
  const [publicalaSyncedUsers, setPublicalaSyncedUsers] = useState([]);
  const [publicalaLoading, setPublicalaLoading] = useState(false);
  
  // DB Diagnostic
  const [dbDiagnostic, setDbDiagnostic] = useState(null);
  const [showDbDiagnostic, setShowDbDiagnostic] = useState(false);
  const [loadingDiagnostic, setLoadingDiagnostic] = useState(false);

  // Pool Statistics
  const [poolStats, setPoolStats] = useState(null);
  const [loadingPoolStats, setLoadingPoolStats] = useState(false);

  // Yearly Bonus
  const [yearlyBonusPreview, setYearlyBonusPreview] = useState(null);
  const [loadingYearlyBonus, setLoadingYearlyBonus] = useState(false);
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [distributeConfirmation, setDistributeConfirmation] = useState('');
  const [distributing, setDistributing] = useState(false);

  // Verificar acceso al admin
  useEffect(() => {
    const checkAccess = async () => {
      if (!token) {
        setHasAdminAccess(false);
        setAccessCheckLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/admin/check-access`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasAdminAccess(response.data.has_access);
      } catch (error) {
        console.error('Error checking admin access:', error);
        setHasAdminAccess(false);
      } finally {
        setAccessCheckLoading(false);
      }
    };
    
    checkAccess();
  }, [token]);

  useEffect(() => {
    if (user && token && hasAdminAccess) {
      fetchAdminData();
    }
  }, [user, token, hasAdminAccess]);

  // Filtrar y ordenar usuarios cuando cambien los filtros
  useEffect(() => {
    let result = users;
    
    // Filtro de búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(u => 
        u.full_name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.referral_code?.toLowerCase().includes(term) ||
        u.referrer_code?.toLowerCase().includes(term)
      );
    }
    
    // Filtro por tipo
    if (filterType !== 'all') {
      result = result.filter(u => u.user_type === filterType);
    }
    
    // Filtro por estado
    if (filterStatus !== 'all') {
      result = result.filter(u => u.membership_status === filterStatus);
    }
    
    // Ordenamiento
    if (sortColumn) {
      result = [...result].sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];
        
        // Manejar casos especiales
        if (sortColumn === 'full_name' || sortColumn === 'email' || sortColumn === 'username' || sortColumn === 'referral_code') {
          aValue = (aValue || '').toLowerCase();
          bValue = (bValue || '').toLowerCase();
        } else if (sortColumn === 'created_at') {
          aValue = new Date(aValue || 0).getTime();
          bValue = new Date(bValue || 0).getTime();
        } else if (sortColumn === 'direct_referrals_count') {
          aValue = aValue || 0;
          bValue = bValue || 0;
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    setFilteredUsers(result);
  }, [users, searchTerm, filterType, filterStatus, sortColumn, sortDirection]);
  
  // Función para manejar el ordenamiento
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Si ya está ordenado por esta columna, cambiar dirección
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Nueva columna, empezar descendente
      setSortColumn(column);
      setSortDirection('desc');
    }
  };
  
  // Componente para el encabezado sorteable
  const SortableHeader = ({ column, children }) => {
    const isActive = sortColumn === column;
    return (
      <th 
        className="text-left py-3 px-3 text-white/80 cursor-pointer hover:text-white hover:bg-purple-500/10 transition-colors select-none"
        onClick={() => handleSort(column)}
        data-testid={`sort-header-${column}`}
      >
        <div className="flex items-center gap-1">
          {children}
          {isActive ? (
            sortDirection === 'asc' ? (
              <ArrowUp className="w-3 h-3 text-purple-400" />
            ) : (
              <ArrowDown className="w-3 h-3 text-purple-400" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 text-white/30" />
          )}
        </div>
      </th>
    );
  };

  const fetchAdminData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, usersRes, transactionsRes, noReferralRes, breakdownRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/transactions`, { headers }),
        axios.get(`${API}/admin/users-without-referral`, { headers }).catch(() => ({ data: { users: [] } })),
        axios.get(`${API}/admin/revenue-breakdown`, { headers }).catch(() => ({ data: null }))
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setFilteredUsers(usersRes.data);
      setTransactions(transactionsRes.data.transactions || transactionsRes.data || []);
      setUsersWithoutReferral(noReferralRes.data.users || []);
      setRevenueBreakdown(breakdownRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (!error.response || error.response?.status >= 500) {
        toast.error('🔄 El sistema se está actualizando. Re-intenta en 2 minutos.');
      } else {
        toast.error('Error al cargar los datos');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Fetch ViubuxPlay Stats
  const fetchViubuxplayStats = async () => {
    setLoadingViubuxplayStats(true);
    try {
      const response = await axios.get(`${API}/admin/viubuxplay/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViubuxplayStats(response.data);
    } catch (error) {
      console.error('Error fetching ViubuxPlay stats:', error);
      toast.error('Error al cargar estadísticas de ViubuxPlay');
    } finally {
      setLoadingViubuxplayStats(false);
    }
  };

  // Export functions
  const handleExportCSV = async (type) => {
    try {
      toast.info(`Generando reporte de ${type}...`);
      const response = await axios.get(
        `${API}/admin/export/${type}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Reporte de ${type} descargado`);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar');
    }
  };

  const handleActivateMembership = async (userId, userType) => {
    const paymentType = userType === 'author' ? 'enrollment' : 'subscription';
    const amount = userType === 'author' ? 4997.0 : 199.0;

    try {
      const response = await axios.post(
        `${API}/admin/activate-membership`,
        {
          user_id: userId,
          payment_type: paymentType,
          amount: amount,
          notes: 'Pago en efectivo registrado manualmente'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Membresía activada. Código: ${response.data.access_code}`);
      setShowActivateModal(false);
      setSelectedUser(null);
      fetchAdminData();
    } catch (error) {
      console.error('Error activating membership:', error);
      toast.error('Error al activar membresía');
    }
  };

  // Verificar pago de MercadoPago
  const handleVerifyMpPayment = async () => {
    if (!mpPaymentId.trim()) {
      toast.error('Ingresa el ID de pago de MercadoPago');
      return;
    }
    
    setVerifyingMpPayment(true);
    setMpVerificationResult(null);
    
    try {
      const response = await axios.get(
        `${API}/admin/payments/verify-mercadopago/${mpPaymentId.trim()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMpVerificationResult(response.data);
      
      if (response.data.status === 'approved') {
        toast.success('✅ Pago verificado como APROBADO en MercadoPago');
      } else {
        toast.warning(`Pago tiene estado: ${response.data.status}`);
      }
    } catch (error) {
      console.error('Error verifying MP payment:', error);
      toast.error(error.response?.data?.detail || 'Error al verificar pago');
      setMpVerificationResult({ error: true, message: error.response?.data?.detail });
    } finally {
      setVerifyingMpPayment(false);
    }
  };

  // Activar con verificación de MercadoPago
  const handleActivateWithMpPayment = async (userId) => {
    if (!mpPaymentId.trim()) {
      toast.error('Primero ingresa y verifica el ID de pago');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/payments/activate-membership`,
        {
          user_id: userId,
          payment_provider: 'mercadopago',
          payment_id: mpPaymentId.trim(),
          amount: mpVerificationResult?.amount || null,
          notes: `Activación manual - MP Payment ID: ${mpPaymentId}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`✅ ${response.data.message}`);
      setShowActivateModal(false);
      setSelectedUser(null);
      setMpPaymentId('');
      setMpVerificationResult(null);
      fetchAdminData();
    } catch (error) {
      console.error('Error activating with MP:', error);
      toast.error(error.response?.data?.detail || 'Error al activar membresía');
    }
  };

  // Actualizar status de transacción
  const handleUpdateTransactionStatus = async () => {
    if (!selectedTransaction) return;
    
    setUpdatingTx(true);
    try {
      const txId = selectedTransaction.id || selectedTransaction.external_reference;
      const response = await axios.put(
        `${API}/admin/transactions/${txId}/status`,
        {
          new_status: newTxStatus,
          mercadopago_payment_id: txMpPaymentId || null,
          notes: txNotes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`✅ ${response.data.message}`);
      setShowUpdateTxModal(false);
      setSelectedTransaction(null);
      setNewTxStatus('paid');
      setTxMpPaymentId('');
      setTxNotes('');
      fetchAdminData();
    } catch (error) {
      console.error('Error updating transaction:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar transacción');
    } finally {
      setUpdatingTx(false);
    }
  };

  // Abrir modal de actualización de transacción
  const openUpdateTxModal = (tx) => {
    setSelectedTransaction(tx);
    setNewTxStatus(tx.payment_status === 'pending' ? 'paid' : tx.payment_status);
    setTxMpPaymentId(tx.mercadopago_payment_id || '');
    setTxNotes('');
    setShowUpdateTxModal(true);
  };

  // Devolución de transacción
  const handleRefundTransaction = async (tx) => {
    if (!confirm(`¿Estás seguro de marcar esta transacción como DEVUELTA?\n\nUsuario: ${tx.user_name || tx.user_email}\nMonto: ${formatCurrency(tx.amount)} ${tx.currency}\n\nEsto cancelará las comisiones asociadas.`)) {
      return;
    }

    try {
      await axios.put(
        `${API}/api/admin/transactions/${tx.session_id || tx.id}/refund`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transacción marcada como devuelta');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar devolución');
    }
  };

  // Eliminar transacción
  const handleDeleteTransaction = async (tx) => {
    if (!confirm(`¿Estás seguro de ELIMINAR esta transacción?\n\nUsuario: ${tx.user_name || tx.user_email}\nMonto: ${formatCurrency(tx.amount)} ${tx.currency}\n\nEsta acción no se puede deshacer.`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/api/admin/transactions/${tx.session_id || tx.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transacción eliminada');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar transacción');
    }
  };

  // Editar reporte de regalías
  const handleEditRoyaltyReport = (report) => {
    // Ir a la pestaña de regalías con el reporte seleccionado
    setActiveTab('royalties');
    toast.info('Edita el reporte desde la pestaña Regalías 5BX');
  };

  // Eliminar reporte de regalías
  const handleDeleteRoyaltyReport = async (report) => {
    if (!confirm(`¿Estás seguro de ELIMINAR este reporte de regalías?\n\nAutor: ${report.user_name}\nPeríodo: ${report.period_start} - ${report.period_end}\nMonto: ${formatCurrency(report.amount)}\n\nEsta acción no se puede deshacer.`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/api/admin/royalties/reports/${report.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reporte de regalías eliminado');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar reporte');
    }
  };

  // Sincronizar usuario a App.Viubux
  const handleSyncToPublicala = async (userId, userEmail) => {
    setSyncingUserId(userId);
    try {
      const response = await axios.post(
        `${API}/admin/users/${userId}/sync-publicala`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.already_synced) {
        toast.info(`${userEmail} ya está sincronizado con app.viubux.com`);
      } else {
        toast.success(`✅ ${userEmail} sincronizado a app.viubux.com`);
        if (response.data.temp_password) {
          toast.info(`Contraseña temporal: ${response.data.temp_password}`, { duration: 10000 });
        }
      }
      fetchAdminData();
    } catch (error) {
      console.error('Error syncing to App.Viubux:', error);
      toast.error(error.response?.data?.detail || 'Error al sincronizar');
    } finally {
      setSyncingUserId(null);
    }
  };

  // Obtener diagnóstico de BD
  const handleGetDbDiagnostic = async () => {
    setLoadingDiagnostic(true);
    try {
      const response = await axios.get(
        `${API}/admin/db-diagnostic`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDbDiagnostic(response.data);
      setShowDbDiagnostic(true);
    } catch (error) {
      console.error('Error getting DB diagnostic:', error);
      toast.error(error.response?.data?.detail || 'Error al obtener diagnóstico');
    } finally {
      setLoadingDiagnostic(false);
    }
  };

  const fetchPoolStatistics = async () => {
    setLoadingPoolStats(true);
    try {
      const response = await axios.get(
        `${API}/admin/pool/statistics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPoolStats(response.data);
    } catch (error) {
      console.error('Error fetching pool statistics:', error);
    } finally {
      setLoadingPoolStats(false);
    }
  };

  const fetchYearlyBonusPreview = async () => {
    setLoadingYearlyBonus(true);
    try {
      const response = await axios.get(
        `${API}/admin/pool/yearly-bonus-preview`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setYearlyBonusPreview(response.data);
    } catch (error) {
      console.error('Error fetching yearly bonus preview:', error);
    } finally {
      setLoadingYearlyBonus(false);
    }
  };

  const handleDistributeYearlyBonus = async () => {
    const year = new Date().getFullYear();
    const expectedConfirmation = `DISTRIBUTE BONUS ${year}`;
    
    if (distributeConfirmation !== expectedConfirmation) {
      toast.error(`Escribe exactamente: ${expectedConfirmation}`);
      return;
    }
    
    setDistributing(true);
    try {
      const response = await axios.post(
        `${API}/admin/pool/distribute-yearly-bonus`,
        { year, confirmation: distributeConfirmation },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`🎉 Bono Anual ${year} distribuido: $${response.data.total_distributed.toLocaleString()} MXN a ${response.data.recipients_count} autores`);
      setShowDistributeModal(false);
      setDistributeConfirmation('');
      fetchYearlyBonusPreview();
      fetchPoolStatistics();
    } catch (error) {
      console.error('Error distributing yearly bonus:', error);
      toast.error(error.response?.data?.detail || 'Error al distribuir bono anual');
    } finally {
      setDistributing(false);
    }
  };

  const handleSubsidizeInscription = async (userId) => {
    try {
      const response = await axios.post(
        `${API}/admin/subsidize-inscription`,
        {
          user_id: userId,
          subsidy_type: subsidyType,
          notes: subsidyNotes || `Inscripción subsidiada - ${subsidyType === 'indefinite' ? 'indefinido' : '30 días'}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const subsidyLabel = subsidyType === 'indefinite' ? 'indefinidamente' : 'por 30 días';
      toast.success(`✨ Inscripción subsidiada ${subsidyLabel} para ${response.data.user_name}`);
      setShowActivateModal(false);
      setSelectedUser(null);
      setSubsidyType('30_days');
      setSubsidyNotes('');
      fetchAdminData();
    } catch (error) {
      console.error('Error subsidizing inscription:', error);
      toast.error(error.response?.data?.detail || 'Error al subsidiar inscripción');
    }
  };

  const fetchExpiringMembers = async () => {
    try {
      const response = await axios.get(`${API}/admin/expiring-memberships`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpiringMembers(response.data);
    } catch (error) {
      console.error('Error fetching expiring members:', error);
    }
  };

  const fetchDiscountSettings = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [settingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/referral-discount-settings`, { headers }),
        axios.get(`${API}/admin/discount-stats`, { headers })
      ]);
      
      setDiscountSettings(settingsRes.data);
      setDiscountStats(statsRes.data);
      setNewDiscountPercent(settingsRes.data.discount_percent);
      setNewDiscountMonths(settingsRes.data.discount_months);
      setDiscountActive(settingsRes.data.is_active);
    } catch (error) {
      console.error('Error fetching discount settings:', error);
    }
  };

  const fetchExchangeRate = async () => {
    try {
      const response = await axios.get(`${API}/admin/config/exchange-rate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExchangeRate(response.data.exchange_rate);
      setExchangeRateUpdatedAt(response.data.updated_at);
      setNewExchangeRate(response.data.exchange_rate.toString());
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
    }
  };

  const handleSaveExchangeRate = async () => {
    const rate = parseFloat(newExchangeRate);
    if (isNaN(rate) || rate <= 0) {
      toast.error('Ingresa un tipo de cambio válido mayor a 0');
      return;
    }
    if (rate > 100) {
      toast.error('El tipo de cambio parece demasiado alto (máximo: 100)');
      return;
    }

    setSavingExchangeRate(true);
    try {
      const response = await axios.post(
        `${API}/admin/config/exchange-rate`,
        { exchange_rate: rate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setExchangeRate(rate);
      setEditingExchangeRate(false);
      setExchangeRateUpdatedAt(new Date().toISOString());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar tipo de cambio');
    } finally {
      setSavingExchangeRate(false);
    }
  };

  const handleAssignReferral = async () => {
    if (!userToAssignReferral || !referralCodeToAssign.trim()) {
      toast.error('Por favor ingresa un código de referido');
      return;
    }
    
    setAssigningReferral(true);
    try {
      const response = await axios.post(
        `${API}/admin/assign-referral`,
        {
          user_id: userToAssignReferral.id,
          referral_code: referralCodeToAssign.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Referido asignado: ${response.data.sponsor} (${response.data.sponsor_type})`);
      setShowAssignReferralModal(false);
      setUserToAssignReferral(null);
      setReferralCodeToAssign('');
      fetchAdminData();
    } catch (error) {
      console.error('Error assigning referral:', error);
      toast.error(error.response?.data?.detail || 'Error al asignar referido');
    } finally {
      setAssigningReferral(false);
    }
  };

  const openAssignReferralModal = (userObj) => {
    setUserToAssignReferral(userObj);
    setReferralCodeToAssign('');
    setShowAssignReferralModal(true);
  };

  // ============== CREATE USER ==============
  const handleCreateUser = async () => {
    if (!newUserData.email || !newUserData.password || !newUserData.full_name || !newUserData.username) {
      toast.error('Todos los campos son requeridos');
      return;
    }
    
    setCreatingUser(true);
    try {
      const payload = {
        email: newUserData.email,
        password: newUserData.password,
        full_name: newUserData.full_name,
        username: newUserData.username,
        user_type: newUserData.user_type,
      };
      if (newUserData.user_type === 'author') {
        payload.author_tier = newUserData.author_tier || 'premium';
      }
      if (newUserData.user_type === 'editorial') {
        payload.editorial_tier = newUserData.editorial_tier || 'lite';
      }
      if (newUserData.activate) {
        payload.membership_status = 'active';
      }
      const response = await axios.post(
        `${API}/admin/create-user`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Usuario creado: ${response.data.email}`);
      setShowCreateUserModal(false);
      setNewUserData({ email: '', password: '', full_name: '', username: '', user_type: 'consumer', author_tier: 'premium', activate: false });
      fetchAdminData();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setCreatingUser(false);
    }
  };

  // ============== CHANGE USER TYPE ==============
  const handleChangeUserType = async (userId, newType) => {
    try {
      const response = await axios.post(
        `${API}/admin/change-user-type`,
        { user_id: userId, new_type: newType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Tipo cambiado: ${response.data.old_type} → ${response.data.new_type}`);
      fetchAdminData();
    } catch (error) {
      console.error('Error changing user type:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar tipo');
    }
  };

  // ============== BULK ACTIONS ==============
  const toggleSelectUser = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.length === filteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map(u => u.id));
    }
  };

  const handleBulkAction = async (action, value = null) => {
    if (selectedUserIds.length === 0) {
      toast.error('Selecciona al menos un usuario');
      return;
    }
    
    const confirmMsg = action === 'delete' 
      ? `¿Eliminar ${selectedUserIds.length} usuarios? Esta acción no se puede deshacer.`
      : `¿Aplicar "${action}" a ${selectedUserIds.length} usuarios?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const response = await axios.post(
        `${API}/admin/bulk-update-users`,
        { user_ids: selectedUserIds, action, value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Acción completada: ${response.data.success} éxitos, ${response.data.failed} fallos`);
      setSelectedUserIds([]);
      fetchAdminData();
    } catch (error) {
      console.error('Error in bulk action:', error);
      toast.error(error.response?.data?.detail || 'Error en acción masiva');
    }
  };

  // ===================== TRANSACTIONS BULK ACTIONS =====================
  const toggleTransactionSelection = (txId) => {
    setSelectedTransactionIds(prev =>
      prev.includes(txId)
        ? prev.filter(id => id !== txId)
        : [...prev, txId]
    );
  };

  const toggleSelectAllTransactions = () => {
    if (selectedTransactionIds.length === transactions.length) {
      setSelectedTransactionIds([]);
    } else {
      setSelectedTransactionIds(transactions.map(t => t.id || t.session_id));
    }
  };

  const handleBulkTransactionAction = async (action) => {
    if (selectedTransactionIds.length === 0) {
      toast.error('Selecciona al menos una transacción');
      return;
    }
    
    const actionMessages = {
      mark_paid: `¿Marcar ${selectedTransactionIds.length} transacciones como PAGADAS?`,
      mark_pending: `¿Marcar ${selectedTransactionIds.length} transacciones como PENDIENTES?`,
      cancel: `¿CANCELAR ${selectedTransactionIds.length} transacciones? Esta acción puede afectar comisiones.`,
      delete: `¿ELIMINAR ${selectedTransactionIds.length} transacciones? Esta acción no se puede deshacer.`
    };
    
    if (!window.confirm(actionMessages[action])) return;
    
    try {
      const response = await axios.post(
        `${API}/admin/bulk-update-transactions`,
        { transaction_ids: selectedTransactionIds, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Acción completada: ${response.data.success} éxitos, ${response.data.failed} fallos`);
      setSelectedTransactionIds([]);
      fetchAdminData();
    } catch (error) {
      console.error('Error in bulk transaction action:', error);
      toast.error(error.response?.data?.detail || 'Error en acción masiva de transacciones');
    }
  };

  // ===================== COMMISSIONS BULK ACTIONS =====================
  const handleBulkCommissionAction = async (action, commissionIds) => {
    if (!commissionIds || commissionIds.length === 0) {
      toast.error('Selecciona al menos una comisión');
      return;
    }
    
    const actionMessages = {
      approve: `¿Aprobar pago de ${commissionIds.length} comisiones?`,
      mark_completed: `¿Marcar ${commissionIds.length} comisiones como COMPLETADAS/PAGADAS?`,
      cancel: `¿CANCELAR ${commissionIds.length} comisiones?`,
      revert_to_pending: `¿Revertir ${commissionIds.length} comisiones a PENDIENTE?`
    };
    
    if (!window.confirm(actionMessages[action])) return;
    
    try {
      const response = await axios.post(
        `${API}/admin/bulk-update-commissions`,
        { commission_ids: commissionIds, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Acción completada: ${response.data.success} éxitos, ${response.data.failed} fallos`);
      setSelectedCommissionIds([]);
      fetchAdminData();
    } catch (error) {
      console.error('Error in bulk commission action:', error);
      toast.error(error.response?.data?.detail || 'Error en acción masiva de comisiones');
    }
  };

  const handleSaveDiscountSettings = async () => {
    setSavingDiscount(true);
    try {
      const response = await axios.post(
        `${API}/admin/referral-discount-settings`,
        {
          discount_percent: newDiscountPercent,
          discount_months: newDiscountMonths,
          is_active: discountActive
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setDiscountSettings(response.data.settings);
      setEditingDiscount(false);
      fetchDiscountSettings();
    } catch (error) {
      console.error('Error saving discount settings:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSavingDiscount(false);
    }
  };

  const handleSendReminders = async () => {
    setAutomationLoading(true);
    try {
      const response = await axios.post(`${API}/admin/send-renewal-reminders`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error('Error al enviar recordatorios');
    } finally {
      setAutomationLoading(false);
    }
  };

  const handleDeactivateExpired = async () => {
    setAutomationLoading(true);
    try {
      const response = await axios.post(`${API}/admin/deactivate-expired-memberships`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchAdminData();
    } catch (error) {
      console.error('Error deactivating memberships:', error);
      toast.error('Error al desactivar membresías');
    } finally {
      setAutomationLoading(false);
    }
  };

  const handleProcessStalePending = async () => {
    setAutomationLoading(true);
    try {
      const response = await axios.post(`${API}/admin/process-stale-pending-users`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { processed_count, skipped_count, message } = response.data;
      
      if (processed_count > 0) {
        toast.success(`✅ ${processed_count} usuarios procesados. ${message}`);
      } else {
        toast.info('No hay usuarios pendientes con más de 21 días');
      }
      
      if (skipped_count > 0) {
        toast.warning(`⚠️ ${skipped_count} usuarios requieren verificación manual`);
      }
      
      fetchAdminData();
    } catch (error) {
      console.error('Error processing stale pending:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar usuarios pendientes');
    } finally {
      setAutomationLoading(false);
    }
  };

  // ========== SUSCRIPCIONES ANUALES ==========
  const handleDetectAnnualIssues = async () => {
    setLoadingAnnualIssues(true);
    try {
      const response = await axios.get(`${API}/admin/detect-annual-subscriptions-issues`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnualIssues(response.data.issues || []);
      if (response.data.issues_found === 0) {
        toast.success('✅ No se encontraron suscripciones anuales con problemas');
      } else {
        toast.warning(`⚠️ Se encontraron ${response.data.issues_found} suscripciones anuales con fecha incorrecta`);
      }
    } catch (error) {
      console.error('Error detecting annual issues:', error);
      toast.error('Error al detectar problemas');
    } finally {
      setLoadingAnnualIssues(false);
    }
  };

  const handleFixAnnualSubscription = async (email, amount) => {
    setFixingAnnual(email);
    try {
      const response = await axios.post(`${API}/admin/fix-annual-subscription`, {
        user_email: email,
        transaction_amount: parseFloat(amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`✅ Suscripción de ${email} corregida`);
      
      // Mostrar detalles
      const corrections = response.data.results?.corrections_made || [];
      corrections.forEach(c => toast.info(c));
      
      // Actualizar lista
      setAnnualIssues(prev => prev.filter(i => i.email !== email));
      fetchAdminData();
    } catch (error) {
      console.error('Error fixing subscription:', error);
      toast.error(error.response?.data?.detail || 'Error al corregir suscripción');
    } finally {
      setFixingAnnual(null);
    }
  };

  const handleRunExpiredCheck = async () => {
    setRunningExpiredCheck(true);
    try {
      const response = await axios.post(`${API}/admin/run-expired-check`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setExpiredCheckResult(response.data);
      
      const { paused_count, in_grace_period_count } = response.data.summary || {};
      
      if (paused_count > 0) {
        toast.success(`✅ ${paused_count} membresías pausadas por vencimiento`);
      }
      if (in_grace_period_count > 0) {
        toast.warning(`⚠️ ${in_grace_period_count} usuarios en período de gracia`);
      }
      if (paused_count === 0 && in_grace_period_count === 0) {
        toast.info('No hay membresías vencidas que procesar');
      }
      
      fetchAdminData();
    } catch (error) {
      console.error('Error running expired check:', error);
      toast.error(error.response?.data?.detail || 'Error al verificar membresías');
    } finally {
      setRunningExpiredCheck(false);
    }
  };

  const handleSendPaymentReminder = async (userId) => {
    try {
      const response = await axios.post(`${API}/admin/send-payment-reminder/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar recordatorio');
    }
  };

  // Función para resetear contraseña de usuario
  const handleResetPassword = async (userId, userEmail) => {
    if (!window.confirm(`¿Estás seguro de resetear la contraseña de ${userEmail}?`)) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/users/${userId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Mostrar la contraseña temporal
      toast.success(
        <div>
          <p className="font-bold">Contraseña reseteada</p>
          <p className="text-sm">Nueva contraseña temporal:</p>
          <p className="font-mono bg-gray-100 p-1 rounded text-sm select-all">{response.data.temp_password}</p>
        </div>,
        { duration: 15000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al resetear contraseña');
    }
  };

  // Función para cambiar tier de autor (Lite/Premium)
  const handleChangeAuthorTier = async (userId, userEmail, currentTier) => {
    const newTier = currentTier === 'lite' ? 'premium' : 'lite';
    const tierLabel = newTier === 'lite' ? 'Lite (solo regalías)' : 'Premium (acceso completo)';
    
    if (!window.confirm(`¿Cambiar a ${userEmail} a tier ${tierLabel}?`)) {
      return;
    }
    
    try {
      const response = await axios.put(
        `${API}/admin/users/${userId}/author-tier`,
        { author_tier: newTier },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      fetchAdminData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar tier de autor');
    }
  };

  // ========== ADMIN: EDIT AUTHOR PROFILE ==========
  const handleOpenMergeModal = (u) => {
    setMergeSource(u);
    setMergeTargetEmail('');
    setMergeMode('editor');
    setShowMergeModal(true);
  };

  const handleConfirmMerge = async () => {
    if (!mergeSource) return;
    const targetEmail = mergeTargetEmail.trim().toLowerCase();
    if (!targetEmail) {
      toast.error('Ingresa el correo de la cuenta principal');
      return;
    }
    const target = users.find((x) => (x.email || '').toLowerCase() === targetEmail);
    if (!target) {
      toast.error('No se encontró una cuenta con ese correo');
      return;
    }
    if (target.id === mergeSource.id) {
      toast.error('La cuenta principal no puede ser la misma que la duplicada');
      return;
    }
    setMergeLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/users/${target.id}/merge-from/${mergeSource.id}`,
        { convert_to_editor: mergeMode === 'editor', deactivate_source: mergeMode === 'deactivate' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const action = mergeMode === 'editor' ? 'convertida en editor/asistente' : 'desactivada';
      toast.success(`Cuenta fusionada en ${target.full_name}. Duplicada ${action}. Obras reasignadas: ${res.data?.works_reassigned ?? 0}`);
      setShowMergeModal(false);
      fetchAdminData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al fusionar las cuentas');
    } finally {
      setMergeLoading(false);
    }
  };

  const handleOpenEditProfile = (u) => {
    setProfileUser(u);
    setProfileForm({
      profile_photo: u.profile_photo || '',
      cover_image: u.link_page_banner || u.cover_image || '',
      bio: u.bio || '',
      link_title: '',
      link_url: '',
    });
    setWorkForm({ title: '', cover_image: '', public_url: '', formats: [] });
    setShowEditProfileModal(true);
  };

  const handleAdminImgUpload = async (field, file) => {
    if (!file) return;
    setUploadingField(field);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API}/imgtool/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = res.data?.url;
      if (url) {
        if (field === 'work_cover') setWorkForm((p) => ({ ...p, cover_image: url }));
        else setProfileForm((p) => ({ ...p, [field]: url }));
        toast.success('Imagen subida');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al subir imagen');
    } finally {
      setUploadingField('');
    }
  };

  const handleSaveProfile = async () => {
    if (!profileUser) return;
    setSavingProfile(true);
    try {
      await axios.put(
        `${API}/admin/users/${profileUser.id}/profile`,
        {
          profile_photo: profileForm.profile_photo,
          cover_image: profileForm.cover_image,
          bio: profileForm.bio,
          link_title: profileForm.link_title,
          link_url: profileForm.link_url,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Perfil del autor actualizado');
      fetchAdminData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  const ADMIN_WORK_FORMATS = [
    { id: 'ebook', label: 'eBook' },
    { id: 'audiobook', label: 'Audiolibro' },
    { id: 'videobook', label: 'Videolibro' },
    { id: 'printed', label: 'Libro impreso' },
  ];

  const toggleWorkFormat = (fmt) => {
    const exists = workForm.formats.find(f => f.format === fmt);
    const formats = exists
      ? workForm.formats.filter(f => f.format !== fmt)
      : [...workForm.formats, { format: fmt, isbn: '', url: '' }];
    setWorkForm({ ...workForm, formats });
  };

  const updateWorkFormatField = (fmt, field, value) => {
    setWorkForm({
      ...workForm,
      formats: workForm.formats.map(f => f.format === fmt ? { ...f, [field]: value } : f),
    });
  };

  const handleRegisterWork = async () => {
    if (!profileUser) return;
    if (!workForm.title.trim()) { toast.error('El título de la obra es obligatorio'); return; }
    if (workForm.formats.length === 0) { toast.error('Selecciona al menos un formato'); return; }
    setRegisteringWork(true);
    try {
      await axios.post(
        `${API}/admin/works/create-and-assign`,
        {
          title: workForm.title,
          author_id: profileUser.id,
          format_items: workForm.formats,
          formats: workForm.formats.map(f => f.format),
          public_url: workForm.public_url || null,
          cover_image: workForm.cover_image || null,
          content_type: workForm.formats[0]?.format || 'audiobook',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Obra registrada y asignada al autor');
      setWorkForm({ title: '', cover_image: '', public_url: '', formats: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al registrar obra');
    } finally {
      setRegisteringWork(false);
    }
  };

  // ========== COMMISSIONS SUMMARY ==========
  const fetchCommissionsSummary = async (month = '') => {
    setLoadingCommissions(true);
    try {
      const url = month 
        ? `${API}/admin/commissions-summary?month=${month}`
        : `${API}/admin/commissions-summary`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommissionsSummary(response.data);
    } catch (error) {
      console.error('Error fetching commissions:', error);
      toast.error('Error al cargar resumen de comisiones');
    } finally {
      setLoadingCommissions(false);
    }
  };

  const exportCommissionsCSV = () => {
    if (!commissionsSummary) return;
    
    const { top_authors_pool, top_referrers_bonus, summary, period } = commissionsSummary;
    
    let csv = `Resumen de Comisiones - ${period}\n\n`;
    csv += `Bono Rápido (25%),${summary.rapid_bonus.total},${summary.rapid_bonus.count} referidos\n`;
    csv += `Pool de Autores (42%),${summary.author_pool.total},${summary.author_pool.distributions} distribuciones\n`;
    csv += `Total Distribuido,${summary.total_distributed}\n\n`;
    
    csv += `TOP 50 AUTORES - POOL DE AUTORES\n`;
    csv += `Posición,Nombre,Email,Ganancias Pool,Distribuciones\n`;
    top_authors_pool.forEach((a, i) => {
      csv += `${i + 1},${a.name},${a.email},${a.pool_earnings},${a.distributions}\n`;
    });
    
    csv += `\nTOP 50 REFERIDORES - BONO RÁPIDO\n`;
    csv += `Posición,Nombre,Email,Ganancias Bono,Referidos\n`;
    top_referrers_bonus.forEach((r, i) => {
      csv += `${i + 1},${r.name},${r.email},${r.bonus_earnings},${r.referrals}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `comisiones_${period.replace(/\s/g, '_')}.csv`;
    link.click();
    toast.success('CSV exportado');
  };

  // Load commissions when stats tab is active
  useEffect(() => {
    if (activeTab === 'stats' && !commissionsSummary) {
      fetchCommissionsSummary(commissionsMonth);
    }
  }, [activeTab]);

  // Funciones para borrar usuario
  const handleOpenDeleteModal = async (user) => {
    setUserToDelete(user);
    setDeletePin('');
    setDeletePreview(null);
    setShowDeleteModal(true);
    
    // Cargar preview de datos a borrar
    try {
      const response = await axios.get(`${API}/admin/user-delete-preview/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletePreview(response.data);
    } catch (error) {
      console.error('Error loading delete preview:', error);
      toast.error('Error al cargar información del usuario');
    }
  };

  const handleDeleteUser = async () => {
    if (deletePin.length !== 4) {
      toast.error('El PIN debe tener 4 dígitos');
      return;
    }
    
    setDeleteLoading(true);
    try {
      const response = await axios.post(`${API}/admin/delete-user`, {
        user_id: userToDelete.id,
        confirmation_pin: deletePin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setShowDeleteModal(false);
      setUserToDelete(null);
      setDeletePin('');
      fetchAdminData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Error al borrar usuario');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ========== FREE TRIAL SETTINGS ==========
  const fetchTrialSettings = async () => {
    setLoadingTrialSettings(true);
    try {
      const response = await axios.get(`${API}/admin/free-trial-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrialSettings(response.data);
    } catch (error) {
      console.error('Error fetching trial settings:', error);
    } finally {
      setLoadingTrialSettings(false);
    }
  };

  const handleSaveTrialSettings = async () => {
    setSavingTrialSettings(true);
    try {
      const response = await axios.post(`${API}/admin/free-trial-settings`, trialSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error saving trial settings:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSavingTrialSettings(false);
    }
  };

  // Load trial settings on settings tab
  useEffect(() => {
    if (activeTab === 'settings') {
      fetchTrialSettings();
      fetchSimulatorMetricsSettings();
    }
  }, [activeTab]);

  // ========== SIMULATOR METRICS VISIBILITY ==========
  const fetchSimulatorMetricsSettings = async () => {
    setLoadingSimulatorSettings(true);
    try {
      const response = await axios.get(`${API}/admin/settings/simulator-metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSimulatorMetricsVisible(response.data.is_visible);
    } catch (error) {
      console.error('Error fetching simulator metrics settings:', error);
    } finally {
      setLoadingSimulatorSettings(false);
    }
  };

  const handleSaveSimulatorMetricsSettings = async (newValue) => {
    setSavingSimulatorSettings(true);
    try {
      const response = await axios.put(
        `${API}/admin/settings/simulator-metrics`, 
        { is_visible: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSimulatorMetricsVisible(newValue);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error saving simulator metrics settings:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSavingSimulatorSettings(false);
    }
  };

  // ========== PAID BUT INACTIVE DETECTION ==========
  const detectPaidButInactive = async () => {
    setLoadingPaidInactive(true);
    try {
      const response = await axios.get(`${API}/admin/detect-paid-but-inactive`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaidButInactive(response.data.issues || []);
      if (response.data.issues_found === 0) {
        toast.success('✅ No se encontraron inconsistencias');
      } else {
        toast.warning(`⚠️ Se encontraron ${response.data.issues_found} usuarios con pagos pero inactivos`);
      }
    } catch (error) {
      console.error('Error detecting paid-but-inactive:', error);
      toast.error('Error al detectar inconsistencias');
    } finally {
      setLoadingPaidInactive(false);
    }
  };

  const fixPaidButInactive = async (userId, userEmail) => {
    setFixingUser(userId);
    try {
      const response = await axios.post(`${API}/admin/fix-paid-but-inactive?user_id=${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`✅ ${userEmail} activado correctamente`);
      setPaidButInactive(prev => prev.filter(u => u.user_id !== userId));
      fetchAdminData();
    } catch (error) {
      console.error('Error fixing user:', error);
      toast.error(error.response?.data?.detail || 'Error al corregir usuario');
    } finally {
      setFixingUser(null);
    }
  };

  const fixAllPaidButInactive = async () => {
    if (!window.confirm(`¿Estás seguro de corregir ${paidButInactive.length} usuarios automáticamente?`)) {
      return;
    }
    
    setFixingAll(true);
    try {
      const response = await axios.post(`${API}/admin/fix-all-paid-but-inactive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`✅ ${response.data.fixed_count} usuarios corregidos`);
      if (response.data.errors_count > 0) {
        toast.warning(`⚠️ ${response.data.errors_count} errores`);
      }
      setPaidButInactive([]);
      fetchAdminData();
    } catch (error) {
      console.error('Error fixing all:', error);
      toast.error('Error al corregir usuarios');
    } finally {
      setFixingAll(false);
    }
  };

  // Función para resetear finanzas
  const handleResetFinances = async () => {
    if (resetFinancesPin.length !== 4) {
      toast.error('El PIN debe tener 4 dígitos');
      return;
    }
    
    if (resetFinancesConfirmText !== 'REINICIAR') {
      toast.error('Debes escribir REINICIAR para confirmar');
      return;
    }
    
    setResetFinancesLoading(true);
    try {
      const response = await axios.post(`${API}/admin/reset-finances`, {
        confirmation_pin: resetFinancesPin,
        reset_transactions: resetFinancesOptions.reset_transactions,
        reset_commissions: resetFinancesOptions.reset_commissions,
        reset_user_balances: resetFinancesOptions.reset_user_balances,
        reset_memberships: resetFinancesOptions.reset_memberships,
        reset_royalty_reports: resetFinancesOptions.reset_royalty_reports
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('¡Finanzas reiniciadas correctamente! Se creó un backup automático.');
      setShowResetFinancesModal(false);
      setResetFinancesPin('');
      setResetFinancesConfirmText('');
      setResetFinancesOptions({
        reset_transactions: true,
        reset_commissions: true,
        reset_user_balances: true,
        reset_memberships: true,
        reset_royalty_reports: true
      });
      fetchAdminData(); // Refrescar datos
      fetchFinanceBackups(); // Refrescar lista de backups
    } catch (error) {
      console.error('Error resetting finances:', error);
      toast.error(error.response?.data?.detail || 'Error al reiniciar finanzas');
    } finally {
      setResetFinancesLoading(false);
    }
  };

  // Función para cargar backups de finanzas
  const fetchFinanceBackups = async () => {
    setLoadingBackups(true);
    try {
      const response = await axios.get(`${API}/admin/finance-backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFinanceBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error fetching finance backups:', error);
    } finally {
      setLoadingBackups(false);
    }
  };

  // Función para descargar un backup
  const downloadBackup = async (backupId, backupType, filename) => {
    try {
      const response = await axios.get(`${API}/admin/finance-backups/${backupId}/${backupType}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Descargando ${filename}`);
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Error al descargar el backup');
    }
  };

  // Funciones para Publica.la Sync
  const fetchPublicalaStats = async () => {
    setPublicalaLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, eventsRes, usersRes] = await Promise.all([
        axios.get(`${API}/webhook/publicala/stats`, { headers }),
        axios.get(`${API}/webhook/publicala/sync-history?limit=20`, { headers }),
        axios.get(`${API}/webhook/publicala/synced-users?limit=20`, { headers })
      ]);
      
      setPublicalaStats(statsRes.data.stats || null);
      setPublicalaEvents(eventsRes.data.events || []);
      setPublicalaSyncedUsers(usersRes.data.users || []);
    } catch (error) {
      console.error('Error fetching App.Viubux data:', error);
      toast.error('Error al cargar datos de App.Viubux');
    } finally {
      setPublicalaLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'automation') {
      fetchExpiringMembers();
    }
    if (activeTab === 'settings') {
      fetchDiscountSettings();
      fetchExchangeRate();
    }
    if (activeTab === 'proofs') {
      fetchBankProofs();
    }
    if (activeTab === 'stats') {
      fetchPoolStatistics();
      fetchYearlyBonusPreview();
    }
  }, [activeTab]);

  const fetchBankProofs = async () => {
    setLoadingProofs(true);
    try {
      const response = await axios.get(`${API}/admin/bank-transfer-proofs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankProofs(response.data.proofs || []);
    } catch (error) {
      console.error('Error fetching bank proofs:', error);
      toast.error('Error al cargar comprobantes');
    } finally {
      setLoadingProofs(false);
    }
  };

  const fetchProofDetail = async (proofId) => {
    try {
      const response = await axios.get(`${API}/admin/bank-transfer-proofs/${proofId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedProof(response.data);
      setShowProofModal(true);
    } catch (error) {
      toast.error('Error al cargar detalle del comprobante');
    }
  };

  const handleVerifyProof = async (proofId, approved) => {
    setProcessingProof(true);
    try {
      const response = await axios.post(
        `${API}/admin/bank-transfer-proofs/${proofId}/verify?approved=${approved}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowProofModal(false);
      setSelectedProof(null);
      fetchBankProofs();
      fetchAdminData(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar comprobante');
    } finally {
      setProcessingProof(false);
    }
  };

  // Pantalla de carga mientras se verifica acceso
  if (accessCheckLoading) {
    return (
      <div className="min-h-screen dark bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Verificando permisos...</div>
      </div>
    );
  }

  // Pantalla de acceso denegado
  if (!hasAdminAccess) {
    const handleBootstrapAdmin = async () => {
      try {
        const response = await axios.post(`${API}/admin/bootstrap`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(response.data.message);
        // Recargar la página para verificar el nuevo acceso
        window.location.reload();
      } catch (error) {
        console.error('Error bootstrapping admin:', error);
        toast.error(error.response?.data?.detail || 'Error al configurar administrador');
      }
    };

    return (
      <div className="min-h-screen dark flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #080c2d 0%, #1a1a3e 100%)' }}>
        <div className="text-center max-w-md p-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
            <UserX className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="gotham-bold text-3xl text-white mb-4">Acceso Denegado</h1>
          <p className="verdana-regular text-white/70 mb-6">
            No tienes permisos para acceder al panel de administración.
          </p>
          <div className="space-y-3">
            <Button
              onClick={handleBootstrapAdmin}
              className="w-full btn-viubux"
              data-testid="bootstrap-admin-btn"
            >
              Convertirme en Administrador
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="w-full border-purple-500/50 text-white hover:bg-purple-500/20"
              data-testid="back-to-dashboard-btn"
            >
              Volver al Dashboard
            </Button>
          </div>
          <p className="verdana-regular text-white/50 text-xs mt-4">
            * Solo funciona si no hay ningún administrador en el sistema
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen dark bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen dark" style={{ background: 'linear-gradient(135deg, #080c2d 0%, #1a1a3e 100%)' }}>
      {/* Header */}
      <header className="border-b border-purple-500/20 backdrop-blur-sm" style={{ background: 'rgba(39, 43, 150, 0.3)' }}>
        <div className="container mx-auto px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img 
              src="/viubux_logo_web.png" 
              alt="Viubux Connect" 
              className="h-10 md:h-12 w-auto object-contain brightness-0 invert"
              style={{ maxWidth: '180px' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<span class="gotham-bold text-2xl text-white">ViubuxConnect</span>';
              }}
            />
            <span className="text-white/50 hidden md:inline">|</span>
            <span className="gotham-medium text-white hidden md:inline">Admin</span>
            <Button
              data-testid="back-to-dashboard"
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-purple-500/50 text-white hover:bg-purple-500/20 text-sm"
            >
              Volver al Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <span className="verdana-regular text-white/80 hidden md:block">{user?.email}</span>
            <Button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="border-purple-500/50 text-white hover:bg-purple-500/20"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          <Card className="card-viubux-dark border-purple-500/20" data-testid="admin-stat-users">
            <CardHeader className="pb-3">
              <CardTitle className="gotham-medium text-white flex items-center gap-2">
                <Users className="w-5 h-5" style={{ color: '#7012b2' }} />
                Total Usuarios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="gotham-black text-4xl text-white mb-1">{stats?.total_users || 0}</div>
              <div className="verdana-regular text-sm text-white/60">
                {stats?.total_authors || 0} autores, {stats?.total_consumers || 0} audio-lectores
              </div>
            </CardContent>
          </Card>

          <Card className="card-viubux-dark border-purple-500/20" data-testid="admin-stat-active">
            <CardHeader className="pb-3">
              <CardTitle className="gotham-medium text-white flex items-center gap-2">
                <UserCheck className="w-5 h-5" style={{ color: '#10b981' }} />
                Usuarios Activos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="gotham-black text-4xl text-white mb-1">{stats?.active_users || 0}</div>
              <div className="verdana-regular text-sm text-white/60">
                {stats?.total_users ? ((stats.active_users / stats.total_users) * 100).toFixed(1) : 0}% del total
              </div>
            </CardContent>
          </Card>

          <Card className="card-viubux-dark border-purple-500/20" data-testid="admin-stat-revenue">
            <CardHeader className="pb-3">
              <CardTitle className="gotham-medium text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5" style={{ color: '#7012b2' }} />
                Ingresos Totales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="gotham-black text-4xl text-white mb-1">{formatCurrency(stats?.total_revenue)}</div>
              <div className="verdana-regular text-sm text-white/60 mb-2">MXN</div>
              {/* Desglose de ingresos */}
              <div className="pt-2 border-t border-purple-500/20 space-y-1 mb-3">
                <div className="flex justify-between text-xs">
                  <span className="text-purple-400">Membresías:</span>
                  <span className="text-white">{formatCurrency((stats?.total_revenue || 0) - (stats?.royalties_total || 0))}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-yellow-400">Regalías (Ventas):</span>
                  <span className="text-white">{formatCurrency(stats?.royalties_total || 0)}</span>
                </div>
              </div>
              <Button 
                onClick={() => setShowRevenueDetail(!showRevenueDetail)}
                variant="outline"
                size="sm"
                className="text-purple-400 border-purple-500/30 hover:bg-purple-500/10"
              >
                <Eye className="w-4 h-4 mr-2" />
                {showRevenueDetail ? 'Ocultar Desglose' : 'Ver Desglose'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Breakdown Section */}
        {showRevenueDetail && revenueBreakdown && (
          <Card className="card-viubux-dark border-purple-500/30 mb-8">
            <CardHeader>
              <CardTitle className="gotham-bold text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-400" />
                Desglose de Ingresos
              </CardTitle>
              <CardDescription className="text-white/60">
                Total: {formatCurrency(revenueBreakdown.total_revenue)} MXN de {revenueBreakdown.transaction_count} transacciones
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {Object.entries(revenueBreakdown.breakdown).map(([key, data]) => (
                  data.count > 0 && (
                    <div 
                      key={key}
                      className={`p-4 rounded-xl border ${
                        key === 'enrollment' ? 'border-amber-500/30 bg-amber-500/5' :
                        key === 'subscription' ? 'border-blue-500/30 bg-blue-500/5' :
                        key === 'subscription_annual' ? 'border-green-500/30 bg-green-500/5' :
                        'border-gray-500/30 bg-gray-500/5'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white/60 text-sm">{data.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          key === 'enrollment' ? 'bg-amber-500/20 text-amber-300' :
                          key === 'subscription' ? 'bg-blue-500/20 text-blue-300' :
                          key === 'subscription_annual' ? 'bg-green-500/20 text-green-300' :
                          'bg-gray-500/20 text-gray-300'
                        }`}>
                          {data.percentage}%
                        </span>
                      </div>
                      <div className="gotham-bold text-2xl text-white mb-1">
                        {formatCurrency(data.total)}
                      </div>
                      <div className="text-white/40 text-xs">
                        {data.count} {data.count === 1 ? 'transacción' : 'transacciones'}
                      </div>
                    </div>
                  )
                ))}
              </div>

              {/* Tabla de transacciones recientes por categoría */}
              <div className="space-y-4">
                {Object.entries(revenueBreakdown.breakdown).map(([key, data]) => (
                  data.transactions.length > 0 && (
                    <div key={key} className="border border-white/10 rounded-lg overflow-hidden">
                      <div className={`px-4 py-2 ${
                        key === 'enrollment' ? 'bg-amber-500/10' :
                        key === 'subscription' ? 'bg-blue-500/10' :
                        key === 'subscription_annual' ? 'bg-green-500/10' :
                        'bg-gray-500/10'
                      }`}>
                        <span className="gotham-bold text-white">{data.label}</span>
                        <span className="text-white/60 ml-2">({data.count} total)</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left py-2 px-4 text-white/60 text-xs font-normal">Usuario</th>
                              <th className="text-left py-2 px-4 text-white/60 text-xs font-normal">Email</th>
                              <th className="text-left py-2 px-4 text-white/60 text-xs font-normal">Fecha</th>
                              <th className="text-right py-2 px-4 text-white/60 text-xs font-normal">Monto</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.transactions.slice(0, 5).map((tx, idx) => (
                              <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                                <td className="py-2 px-4 text-white text-sm">{tx.user_name}</td>
                                <td className="py-2 px-4 text-white/60 text-sm">{tx.user_email}</td>
                                <td className="py-2 px-4 text-white/60 text-sm">{tx.date}</td>
                                <td className="py-2 px-4 text-right text-white gotham-bold">{formatCurrency(tx.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Financial Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="card-viubux-dark border-yellow-500/30 bg-gradient-to-br from-yellow-900/10 to-transparent" data-testid="admin-stat-pending-payout">
            <CardHeader className="pb-2">
              <CardTitle className="gotham-medium text-white text-sm flex items-center gap-2">
                <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Por Pagar (Total)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="gotham-black text-2xl text-yellow-400">{formatCurrency(stats?.total_pending_payout)}</div>
              <div className="verdana-regular text-xs text-white/50 mt-1 space-y-0.5">
                <div>{stats?.pending_payout_count || 0} afiliados</div>
                <div className="text-yellow-300/70">• Comisiones: {formatCurrency((stats?.total_pending_payout || 0) - (stats?.royalties_pending || 0))}</div>
                <div className="text-orange-300/70">• Regalías: {formatCurrency(stats?.royalties_pending || 0)}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-viubux-dark border-green-500/30 bg-gradient-to-br from-green-900/10 to-transparent" data-testid="admin-stat-paid">
            <CardHeader className="pb-2">
              <CardTitle className="gotham-medium text-white text-sm flex items-center gap-2">
                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Ya Pagado (Total)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="gotham-black text-2xl text-green-400">{formatCurrency(stats?.total_already_paid)}</div>
              <div className="verdana-regular text-xs text-white/50 mt-1 space-y-0.5">
                <div>A afiliados y autores</div>
                <div className="text-green-300/70">• Comisiones: {formatCurrency((stats?.total_already_paid || 0) - (stats?.royalties_paid || 0))}</div>
                <div className="text-emerald-300/70">• Regalías: {formatCurrency(stats?.royalties_paid || 0)}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-viubux-dark border-purple-500/30 bg-gradient-to-br from-purple-900/10 to-transparent" data-testid="admin-stat-commissions">
            <CardHeader className="pb-2">
              <CardTitle className="gotham-medium text-white text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-purple-400" />
                Comisiones Generadas
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="gotham-black text-2xl text-purple-400">{formatCurrency((stats?.rapid_bonus_total || 0) + (stats?.pool_total || 0))}</div>
              <div className="verdana-regular text-xs text-white/50 mt-1">
                Bono: {formatCurrency(stats?.rapid_bonus_total)} + Pool: {formatCurrency(stats?.pool_total)}
              </div>
            </CardContent>
          </Card>

          <Card className="card-viubux-dark border-blue-500/30 bg-gradient-to-br from-blue-900/10 to-transparent" data-testid="admin-stat-profit">
            <CardHeader className="pb-2">
              <CardTitle className="gotham-medium text-white text-sm flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Utilidad ViubuxConnect
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="gotham-black text-2xl text-blue-400">{formatCurrency(stats?.company_profit)}</div>
              <div className="verdana-regular text-xs text-white/50 mt-1">
                33% de ingresos
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ViubuxPlay Stats Card */}
        <Card className="card-viubux-dark border-cyan-500/30 mb-8" data-testid="viubuxplay-stats-card">
          <CardHeader>
            <CardTitle className="gotham-bold text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                ViubuxPlay - Ventas de Contenido
              </div>
              <Button
                onClick={() => {
                  if (!viubuxplayStats) fetchViubuxplayStats();
                  setShowViubuxplayStats(!showViubuxplayStats);
                }}
                variant="outline"
                size="sm"
                className="text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/10"
                data-testid="toggle-viubuxplay-stats"
              >
                <Eye className="w-4 h-4 mr-2" />
                {showViubuxplayStats ? 'Ocultar' : 'Ver Detalles'}
              </Button>
            </CardTitle>
            <CardDescription className="text-white/60">
              Ventas directas y suscripciones desde play.viubux.com
            </CardDescription>
          </CardHeader>
          
          {showViubuxplayStats && (
            <CardContent>
              {loadingViubuxplayStats ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : viubuxplayStats ? (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
                      <div className="text-cyan-400 text-sm mb-1">Ventas Totales</div>
                      <div className="gotham-bold text-2xl text-white">{viubuxplayStats.summary?.total_content_sales || 0}</div>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                      <div className="text-green-400 text-sm mb-1">Ingresos Brutos</div>
                      <div className="gotham-bold text-2xl text-white">{formatCurrency(viubuxplayStats.summary?.total_gross_revenue)}</div>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                      <div className="text-purple-400 text-sm mb-1">Regalías Autores</div>
                      <div className="gotham-bold text-2xl text-white">{formatCurrency(viubuxplayStats.summary?.total_author_royalties)}</div>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                      <div className="text-blue-400 text-sm mb-1">Ingresos Plataforma</div>
                      <div className="gotham-bold text-2xl text-white">{formatCurrency(viubuxplayStats.summary?.total_platform_revenue)}</div>
                    </div>
                  </div>

                  {/* Content Type Breakdown */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Cursos */}
                    <div className="bg-gradient-to-br from-amber-900/20 to-transparent border border-amber-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        <span className="gotham-bold text-amber-400">CURSOS (87% Autor)</span>
                        <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full ml-auto">
                          Mejor que Hotmart
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-white/50">Ventas</div>
                          <div className="text-white font-bold">{viubuxplayStats.content_breakdown?.cursos?.count || 0}</div>
                        </div>
                        <div>
                          <div className="text-white/50">Ingresos</div>
                          <div className="text-white font-bold">{formatCurrency(viubuxplayStats.content_breakdown?.cursos?.gross)}</div>
                        </div>
                        <div>
                          <div className="text-white/50">Autores (87%)</div>
                          <div className="text-green-400 font-bold">{formatCurrency(viubuxplayStats.content_breakdown?.cursos?.author_royalty)}</div>
                        </div>
                        <div>
                          <div className="text-white/50">Plataforma (13%)</div>
                          <div className="text-blue-400 font-bold">{formatCurrency(viubuxplayStats.content_breakdown?.cursos?.platform_fee)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Regular Content */}
                    <div className="bg-gradient-to-br from-indigo-900/20 to-transparent border border-indigo-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                        </svg>
                        <span className="gotham-bold text-indigo-400">CONTENIDO REGULAR (67% Autor)</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-white/50">Ventas</div>
                          <div className="text-white font-bold">{viubuxplayStats.content_breakdown?.regular?.count || 0}</div>
                        </div>
                        <div>
                          <div className="text-white/50">Ingresos</div>
                          <div className="text-white font-bold">{formatCurrency(viubuxplayStats.content_breakdown?.regular?.gross)}</div>
                        </div>
                        <div>
                          <div className="text-white/50">Autores (67%)</div>
                          <div className="text-green-400 font-bold">{formatCurrency(viubuxplayStats.content_breakdown?.regular?.author_royalty)}</div>
                        </div>
                        <div>
                          <div className="text-white/50">Plataforma (33%)</div>
                          <div className="text-blue-400 font-bold">{formatCurrency(viubuxplayStats.content_breakdown?.regular?.platform_fee)}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subscription Stats */}
                  {viubuxplayStats.subscription_stats?.total_subscriptions > 0 && (
                    <div className="bg-gradient-to-br from-purple-900/20 to-transparent border border-purple-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                        </svg>
                        <span className="gotham-bold text-purple-400">SUSCRIPCIONES VIUBUXPLAY</span>
                      </div>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-sm">
                        <div>
                          <div className="text-white/50">Total</div>
                          <div className="text-white font-bold">{viubuxplayStats.subscription_stats?.total_subscriptions}</div>
                        </div>
                        <div>
                          <div className="text-white/50">Monto</div>
                          <div className="text-white font-bold">{formatCurrency(viubuxplayStats.subscription_stats?.total_amount)}</div>
                        </div>
                        <div>
                          <div className="text-white/50">Nuevos</div>
                          <div className="text-green-400 font-bold">{viubuxplayStats.subscription_stats?.new_users_created}</div>
                        </div>
                        <div>
                          <div className="text-white/50">Con Referido</div>
                          <div className="text-yellow-400 font-bold">{viubuxplayStats.subscription_stats?.with_referrer}</div>
                        </div>
                        <div>
                          <div className="text-white/50">Rapid Bonus (25%)</div>
                          <div className="text-yellow-400 font-bold">{formatCurrency(viubuxplayStats.subscription_stats?.rapid_bonus_total)}</div>
                        </div>
                        <div>
                          <div className="text-white/50">Pool (42%)</div>
                          <div className="text-purple-400 font-bold">{formatCurrency(viubuxplayStats.subscription_stats?.pool_contribution)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Top Authors */}
                  {viubuxplayStats.top_authors?.length > 0 && (
                    <div>
                      <h4 className="gotham-bold text-white mb-3">Top Autores en ViubuxPlay</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left py-2 px-3 text-white/60">#</th>
                              <th className="text-left py-2 px-3 text-white/60">Autor</th>
                              <th className="text-right py-2 px-3 text-white/60">Ventas</th>
                              <th className="text-right py-2 px-3 text-white/60">Ingresos</th>
                              <th className="text-right py-2 px-3 text-white/60">Regalías</th>
                            </tr>
                          </thead>
                          <tbody>
                            {viubuxplayStats.top_authors.slice(0, 5).map((author, idx) => (
                              <tr key={author.name} className="border-b border-white/5">
                                <td className="py-2 px-3 text-white/40">{idx + 1}</td>
                                <td className="py-2 px-3 text-white">{author.name}</td>
                                <td className="py-2 px-3 text-white text-right">{author.sales_count}</td>
                                <td className="py-2 px-3 text-white text-right">{formatCurrency(author.gross)}</td>
                                <td className="py-2 px-3 text-green-400 text-right">{formatCurrency(author.royalty)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Recent Sales */}
                  {viubuxplayStats.recent_sales?.length > 0 && (
                    <div>
                      <h4 className="gotham-bold text-white mb-3">Ventas Recientes</h4>
                      <div className="space-y-2">
                        {viubuxplayStats.recent_sales.slice(0, 5).map((sale, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-black/20 rounded-lg p-3">
                            <div>
                              <div className="text-white font-medium">{sale.content_name}</div>
                              <div className="text-white/50 text-xs flex items-center gap-2">
                                <span className={`px-1.5 py-0.5 rounded ${sale.is_course ? 'bg-amber-500/20 text-amber-300' : 'bg-indigo-500/20 text-indigo-300'}`}>
                                  {sale.is_course ? 'Curso' : sale.content_type}
                                </span>
                                <span>{sale.author_name}</span>
                                <span>{sale.date}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-white font-bold">{formatCurrency(sale.gross)}</div>
                              <div className="text-green-400 text-xs">Autor: {formatCurrency(sale.author_royalty)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-white/50 py-8">
                  No hay datos de ViubuxPlay disponibles
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Regalías / Ventas Directas Summary Card */}
        {stats?.royalties_total > 0 && (
          <div className="mb-8">
            <Card className="card-viubux-dark border-orange-500/30 bg-gradient-to-br from-orange-900/10 to-transparent" data-testid="admin-stat-royalties">
              <CardHeader className="pb-2">
                <CardTitle className="gotham-medium text-white text-sm flex items-center gap-2">
                  <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  📚 Regalías / Ventas Directas (5BX Publishing & Distribution)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="gotham-black text-3xl text-orange-400">{formatCurrency(stats?.royalties_total)}</div>
                    <div className="verdana-regular text-xs text-white/50 mt-1">
                      {stats?.royalties_units_sold || 0} unidades vendidas
                    </div>
                  </div>
                  <div className="text-right space-y-1">
                    <div className="text-sm">
                      <span className="text-white/50">Pagado: </span>
                      <span className="text-emerald-400 gotham-bold">{formatCurrency(stats?.royalties_paid)}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-white/50">Pendiente: </span>
                      <span className="text-orange-400 gotham-bold">{formatCurrency(stats?.royalties_pending)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-purple-500/20 overflow-x-auto">
          <button
            data-testid="admin-tab-stats"
            onClick={() => setActiveTab('stats')}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap ${
              activeTab === 'stats'
                ? 'text-white border-b-2 border-purple-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Estadísticas
          </button>
          {/* Tab Códigos de Acceso - OCULTO por indicación del admin */}
          <button
            data-testid="admin-tab-users"
            onClick={() => setActiveTab('users')}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap ${
              activeTab === 'users'
                ? 'text-white border-b-2 border-purple-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Usuarios
          </button>
          <button
            data-testid="admin-tab-transactions"
            onClick={() => setActiveTab('transactions')}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap ${
              activeTab === 'transactions'
                ? 'text-white border-b-2 border-purple-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Transacciones
          </button>
          <button
            data-testid="admin-tab-proofs"
            onClick={() => setActiveTab('proofs')}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'proofs'
                ? 'text-white border-b-2 border-purple-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <CreditCard className="w-4 h-4" />
            Comprobantes
            {bankProofs.filter(p => p.status === 'pending_verification').length > 0 && (
              <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                {bankProofs.filter(p => p.status === 'pending_verification').length}
              </span>
            )}
          </button>
          <button
            data-testid="admin-tab-payouts"
            onClick={() => setActiveTab('payouts')}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap ${
              activeTab === 'payouts'
                ? 'text-white border-b-2 border-purple-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Gestión de Pagos
          </button>
          <button
            data-testid="admin-tab-automation"
            onClick={() => setActiveTab('automation')}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap ${
              activeTab === 'automation'
                ? 'text-white border-b-2 border-purple-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Automatización
          </button>
          <button
            data-testid="admin-tab-settings"
            onClick={() => setActiveTab('settings')}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap ${
              activeTab === 'settings'
                ? 'text-white border-b-2 border-purple-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Configuración
          </button>
          <button
            data-testid="admin-tab-blog"
            onClick={() => setActiveTab('blog')}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'blog'
                ? 'text-white border-b-2 border-purple-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <Newspaper className="w-4 h-4" />
            Blog/Noticias
          </button>
          <button
            data-testid="admin-tab-verification"
            onClick={() => setActiveTab('verification')}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'verification'
                ? 'text-white border-b-2 border-purple-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            Verificación Autores
          </button>
          <button
            data-testid="admin-tab-royalties"
            onClick={() => setActiveTab('royalties')}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'royalties'
                ? 'text-white border-b-2 border-green-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <Building2 className="w-4 h-4" />
            Regalías 5BX
          </button>
          <button
            data-testid="admin-tab-reconciliation"
            onClick={() => setActiveTab('reconciliation')}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'reconciliation'
                ? 'text-white border-b-2 border-orange-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <PackageSearch className="w-4 h-4" />
            Conciliación
          </button>
          <button
            data-testid="admin-tab-publicala"
            onClick={() => {
              setActiveTab('publicala');
              fetchPublicalaStats();
            }}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'publicala'
                ? 'text-white border-b-2 border-cyan-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <RefreshCw className="w-4 h-4" />
            Sync App.Viubux
          </button>
          <button
            data-testid="admin-tab-play-sync"
            onClick={() => setActiveTab('play_sync')}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'play_sync'
                ? 'text-white border-b-2 border-violet-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <RefreshCw className="w-4 h-4" />
            Sync Play
          </button>
          <button
            data-testid="admin-tab-tools"
            onClick={() => setActiveTab('tools')}
            className={`gotham-medium px-4 py-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'tools'
                ? 'text-white border-b-2 border-emerald-500'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <ImageUp className="w-4 h-4" />
            Tools
          </button>
        </div>

        {/* Content */}
        {activeTab === 'stats' && (
          <>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="card-viubux-dark border-purple-500/20" data-testid="admin-chart-users">
              <CardHeader>
                <CardTitle className="gotham-bold text-white">Distribución de Usuarios</CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Por tipo de membresía
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="verdana-regular text-white">Autores</span>
                    <span className="gotham-bold text-white">{stats?.total_authors || 0}</span>
                  </div>
                  <div className="w-full bg-black/30 rounded-full h-3">
                    <div
                      className="h-3 rounded-full"
                      style={{
                        width: `${stats?.total_users ? (stats.total_authors / stats.total_users) * 100 : 0}%`,
                        background: 'linear-gradient(90deg, #7012b2, #662d91)'
                      }}
                    />
                  </div>
                  
                  <div className="flex justify-between items-center mt-4">
                    <span className="verdana-regular text-white">Audio-Lectores</span>
                    <span className="gotham-bold text-white">{stats?.total_consumers || 0}</span>
                  </div>
                  <div className="w-full bg-black/30 rounded-full h-3">
                    <div
                      className="h-3 rounded-full"
                      style={{
                        width: `${stats?.total_users ? (stats.total_consumers / stats.total_users) * 100 : 0}%`,
                        background: 'linear-gradient(90deg, #272b96, #7012b2)'
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-viubux-dark border-purple-500/20" data-testid="admin-chart-activity">
              <CardHeader>
                <CardTitle className="gotham-bold text-white">Estado de Membresías</CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Activas vs Inactivas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="verdana-regular text-white flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-green-500" />
                      Activas
                    </span>
                    <span className="gotham-bold text-white">{stats?.active_users || 0}</span>
                  </div>
                  <div className="w-full bg-black/30 rounded-full h-3">
                    <div
                      className="bg-green-500 h-3 rounded-full"
                      style={{
                        width: `${stats?.total_users ? (stats.active_users / stats.total_users) * 100 : 0}%`
                      }}
                    />
                  </div>
                  
                  <div className="flex justify-between items-center mt-4">
                    <span className="verdana-regular text-white flex items-center gap-2">
                      <UserX className="w-4 h-4 text-red-500" />
                      Inactivas
                    </span>
                    <span className="gotham-bold text-white">{(stats?.total_users || 0) - (stats?.active_users || 0)}</span>
                  </div>
                  <div className="w-full bg-black/30 rounded-full h-3">
                    <div
                      className="bg-red-500 h-3 rounded-full"
                      style={{
                        width: `${stats?.total_users ? ((stats.total_users - stats.active_users) / stats.total_users) * 100 : 0}%`
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Pool Statistics Card */}
          <Card className="card-viubux-dark border-purple-500/30 mt-6" data-testid="admin-pool-stats">
            <CardHeader>
              <CardTitle className="gotham-bold text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-400" />
                Pool de Autores - Estadísticas
              </CardTitle>
              <CardDescription className="verdana-regular text-white/70">
                Distribución del 42% de ingresos entre autores activos
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingPoolStats ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
                  <span className="ml-2 text-white/60">Cargando estadísticas...</span>
                </div>
              ) : poolStats ? (
                <div className="space-y-6">
                  {/* Main Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 text-center">
                      <div className="text-white/60 text-sm mb-1">Pool Total (Histórico + Nuevo)</div>
                      <div className="gotham-bold text-2xl text-purple-400">
                        {formatCurrency(poolStats.combined?.total_pool_ever || 0)}
                      </div>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                      <div className="text-white/60 text-sm mb-1">Distribuido (Nuevo Sistema)</div>
                      <div className="gotham-bold text-2xl text-green-400">
                        {formatCurrency(poolStats.forward_looking?.total_distributed || 0)}
                      </div>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                      <div className="text-white/60 text-sm mb-1">No Distribuido (Reserva)</div>
                      <div className="gotham-bold text-2xl text-yellow-400">
                        {formatCurrency(poolStats.combined?.undistributed_reserve || 0)}
                      </div>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                      <div className="text-white/60 text-sm mb-1">Eficiencia de Distribución</div>
                      <div className="gotham-bold text-2xl text-blue-400">
                        {poolStats.forward_looking?.distribution_efficiency || 0}%
                      </div>
                    </div>
                  </div>

                  {/* Referral Multiplier Explanation */}
                  <div className="bg-black/30 border border-white/10 rounded-lg p-4">
                    <h4 className="gotham-bold text-white mb-3 flex items-center gap-2">
                      <Info className="w-4 h-4 text-blue-400" />
                      Multiplicador de Referidos (% del Pool Personal)
                    </h4>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center text-xs">
                      <div className="bg-red-500/10 rounded p-2">
                        <div className="text-white/50">0 ref (sin susc.)</div>
                        <div className="text-red-300 gotham-bold">5%</div>
                      </div>
                      <div className="bg-orange-500/10 rounded p-2">
                        <div className="text-white/50">0 ref (activo)</div>
                        <div className="text-orange-300 gotham-bold">10%</div>
                      </div>
                      <div className="bg-yellow-500/10 rounded p-2">
                        <div className="text-white/50">1 referido</div>
                        <div className="text-yellow-300 gotham-bold">25%</div>
                      </div>
                      <div className="bg-blue-500/10 rounded p-2">
                        <div className="text-white/50">2 referidos</div>
                        <div className="text-blue-300 gotham-bold">50%</div>
                      </div>
                      <div className="bg-purple-500/10 rounded p-2">
                        <div className="text-white/50">3 referidos</div>
                        <div className="text-purple-300 gotham-bold">75%</div>
                      </div>
                      <div className="bg-green-500/10 rounded p-2">
                        <div className="text-white/50">4+ referidos</div>
                        <div className="text-green-300 gotham-bold">100%</div>
                      </div>
                    </div>
                    <p className="text-white/40 text-xs mt-3">
                      💡 El dinero no distribuido (debido a que los autores no alcanzan el 100%) queda en reserva.
                    </p>
                  </div>

                  {/* Reserve Split Section */}
                  <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-lg p-4">
                    <h4 className="gotham-bold text-white mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-purple-400" />
                      Distribución de Reserva (No Distribuido)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-black/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white/60 text-sm">Plataforma (Viubux)</span>
                          <span className="text-purple-300 gotham-bold">33%</span>
                        </div>
                        <div className="gotham-bold text-2xl text-purple-400">
                          {formatCurrency(poolStats.reserve_split?.platform_reserve || 0)}
                        </div>
                        <p className="text-white/40 text-xs mt-2">Retención para operación</p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white/60 text-sm">Bono Anual (Diciembre)</span>
                          <span className="text-green-300 gotham-bold">67%</span>
                        </div>
                        <div className="gotham-bold text-2xl text-green-400">
                          {formatCurrency(poolStats.reserve_split?.yearly_bonus_pool || 0)}
                        </div>
                        <p className="text-white/40 text-xs mt-2">Top 20 earners (prorrateado por ingresos)</p>
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <p className="text-blue-300 text-xs flex items-start gap-2">
                        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                          <strong>Bono Anual de Diciembre:</strong> El 67% de la reserva se distribuye entre los 20 autores con mayores ingresos totales del año, 
                          prorrateado según sus ganancias. Ejemplo: Si el autor tiene $10K de $18K totales, recibe 10/18 del bono.
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Top 20 Leaderboard - Yearly Bonus Preview */}
                  <Card className="card-viubux-dark border-yellow-500/30 bg-gradient-to-r from-yellow-900/10 to-orange-900/10">
                    <CardHeader>
                      <CardTitle className="gotham-bold text-white flex items-center gap-2">
                        <Award className="w-5 h-5 text-yellow-400" />
                        Top 20 Leaderboard - Bono Anual {new Date().getFullYear()}
                      </CardTitle>
                      <CardDescription className="verdana-regular text-white/70">
                        Autores con mayores ingresos y su bono proyectado
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loadingYearlyBonus ? (
                        <div className="flex items-center justify-center py-8">
                          <RefreshCw className="w-6 h-6 text-yellow-400 animate-spin" />
                          <span className="ml-2 text-white/60">Cargando leaderboard...</span>
                        </div>
                      ) : yearlyBonusPreview?.already_distributed ? (
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                          <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                          <p className="text-green-400 gotham-bold">Bono {yearlyBonusPreview.year} ya distribuido</p>
                          <p className="text-white/60 text-sm mt-1">{yearlyBonusPreview.message}</p>
                        </div>
                      ) : yearlyBonusPreview ? (
                        <div className="space-y-4">
                          {/* Summary Stats */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-yellow-500/10 rounded-lg p-3 text-center">
                              <div className="text-white/50 text-xs">Pool Bono Anual</div>
                              <div className="gotham-bold text-xl text-yellow-400">
                                {formatCurrency(yearlyBonusPreview.yearly_bonus_pool)}
                              </div>
                            </div>
                            <div className="bg-blue-500/10 rounded-lg p-3 text-center">
                              <div className="text-white/50 text-xs">Total Ingresos Top 20</div>
                              <div className="gotham-bold text-xl text-blue-400">
                                {formatCurrency(yearlyBonusPreview.top_20_total_income)}
                              </div>
                            </div>
                            <div className="bg-purple-500/10 rounded-lg p-3 text-center">
                              <div className="text-white/50 text-xs">Autores en Top</div>
                              <div className="gotham-bold text-xl text-purple-400">
                                {yearlyBonusPreview.top_20_count}
                              </div>
                            </div>
                            <div className="bg-green-500/10 rounded-lg p-3 text-center">
                              <div className="text-white/50 text-xs">Total con Ingresos</div>
                              <div className="gotham-bold text-xl text-green-400">
                                {yearlyBonusPreview.total_authors_with_income}
                              </div>
                            </div>
                          </div>

                          {/* Leaderboard Table */}
                          {yearlyBonusPreview.leaderboard?.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-yellow-500/20">
                                    <th className="text-left py-2 px-3 text-white/60">#</th>
                                    <th className="text-left py-2 px-3 text-white/60">Autor</th>
                                    <th className="text-right py-2 px-3 text-white/60">Comisiones</th>
                                    <th className="text-right py-2 px-3 text-white/60">Pool</th>
                                    <th className="text-right py-2 px-3 text-white/60">Regalías</th>
                                    <th className="text-right py-2 px-3 text-white/60">Total</th>
                                    <th className="text-right py-2 px-3 text-white/60">% Share</th>
                                    <th className="text-right py-2 px-3 text-yellow-400">Bono Proyectado</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {yearlyBonusPreview.leaderboard.map((author, idx) => (
                                    <tr 
                                      key={author.user_id} 
                                      className={`border-b border-white/5 hover:bg-white/5 ${idx < 3 ? 'bg-yellow-500/5' : ''}`}
                                    >
                                      <td className="py-2 px-3">
                                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : (idx + 1)}
                                      </td>
                                      <td className="py-2 px-3">
                                        <div className="text-white font-medium">{author.full_name || 'Sin nombre'}</div>
                                        <div className="text-white/50 text-xs">{author.email}</div>
                                      </td>
                                      <td className="py-2 px-3 text-right text-green-400">
                                        {formatCurrency(author.commissions)}
                                      </td>
                                      <td className="py-2 px-3 text-right text-purple-400">
                                        {formatCurrency(author.pool_earnings)}
                                      </td>
                                      <td className="py-2 px-3 text-right text-blue-400">
                                        {formatCurrency(author.royalties)}
                                      </td>
                                      <td className="py-2 px-3 text-right text-white gotham-bold">
                                        {formatCurrency(author.total_income)}
                                      </td>
                                      <td className="py-2 px-3 text-right text-white/60">
                                        {author.share_percentage}%
                                      </td>
                                      <td className="py-2 px-3 text-right gotham-bold text-yellow-400">
                                        {formatCurrency(author.projected_bonus)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center py-8 text-white/50">
                              No hay autores con ingresos registrados
                            </div>
                          )}

                          {/* Distribute Button */}
                          {yearlyBonusPreview.yearly_bonus_pool > 0 && yearlyBonusPreview.leaderboard?.length > 0 && (
                            <div className="flex justify-end pt-4 border-t border-yellow-500/20">
                              <Button
                                onClick={() => setShowDistributeModal(true)}
                                className="bg-yellow-600 hover:bg-yellow-700 text-black gotham-bold"
                                data-testid="distribute-yearly-bonus-btn"
                              >
                                <DollarSign className="w-4 h-4 mr-2" />
                                Distribuir Bono Anual {new Date().getFullYear()}
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-white/50">
                          No hay datos disponibles
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Recent Distributions */}
                  {poolStats.recent_distributions?.length > 0 && (
                    <div>
                      <h4 className="gotham-bold text-white mb-3">Distribuciones Recientes</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left py-2 px-3 text-white/60">Fecha</th>
                              <th className="text-right py-2 px-3 text-white/60">Pool</th>
                              <th className="text-right py-2 px-3 text-white/60">Distribuido</th>
                              <th className="text-right py-2 px-3 text-white/60">No Distribuido</th>
                              <th className="text-right py-2 px-3 text-white/60">Autores</th>
                            </tr>
                          </thead>
                          <tbody>
                            {poolStats.recent_distributions.slice(0, 10).map((dist, idx) => (
                              <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                                <td className="py-2 px-3 text-white/70">
                                  {new Date(dist.payment_date).toLocaleDateString('es-MX')}
                                </td>
                                <td className="py-2 px-3 text-right text-purple-400">
                                  {formatCurrency(dist.pool_amount)}
                                </td>
                                <td className="py-2 px-3 text-right text-green-400">
                                  {formatCurrency(dist.total_distributed)}
                                </td>
                                <td className="py-2 px-3 text-right text-yellow-400">
                                  {formatCurrency(dist.undistributed)}
                                </td>
                                <td className="py-2 px-3 text-right text-white/60">
                                  {dist.authors_count}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={fetchPoolStatistics}
                    variant="outline"
                    size="sm"
                    className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualizar Estadísticas
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-white/50">
                  No hay datos de pool disponibles
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* ========== COMMISSIONS SUMMARY DASHBOARD ========== */}
          <Card className="card-viubux-dark border-green-500/30 mt-6" data-testid="commissions-summary-card">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="gotham-bold text-white flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    Resumen de Distribución
                  </CardTitle>
                  <CardDescription className="verdana-regular text-white/70">
                    Bono Rápido (25%) + Pool de Autores (42%)
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={commissionsMonth}
                    onChange={(e) => {
                      setCommissionsMonth(e.target.value);
                      fetchCommissionsSummary(e.target.value);
                    }}
                    className="bg-black/50 border border-white/20 rounded px-3 py-1.5 text-white text-sm"
                  >
                    <option value="">Todo el tiempo</option>
                    {Array.from({length: 12}, (_, i) => {
                      const d = new Date();
                      d.setMonth(d.getMonth() - i);
                      const val = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
                      const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
                      return <option key={val} value={val}>{months[d.getMonth()]} {d.getFullYear()}</option>;
                    })}
                  </select>
                  <Button
                    onClick={exportCommissionsCSV}
                    disabled={!commissionsSummary}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Exportar CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingCommissions ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-green-400" />
                </div>
              ) : commissionsSummary ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-yellow-400 text-sm mb-1">
                        <Zap className="w-4 h-4" />
                        Bono Rápido (25%)
                      </div>
                      <div className="text-2xl gotham-bold text-white">
                        {formatCurrency(commissionsSummary.summary?.rapid_bonus?.total || 0)}
                      </div>
                      <div className="text-white/50 text-xs mt-1">
                        {commissionsSummary.summary?.rapid_bonus?.count || 0} referidos
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-purple-400 text-sm mb-1">
                        <Users className="w-4 h-4" />
                        Pool de Autores (42%)
                      </div>
                      <div className="text-2xl gotham-bold text-white">
                        {formatCurrency(commissionsSummary.summary?.author_pool?.total || 0)}
                      </div>
                      <div className="text-white/50 text-xs mt-1">
                        {commissionsSummary.summary?.author_pool?.distributions || 0} distribuciones
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-green-500/20 to-teal-500/20 border border-green-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-green-400 text-sm mb-1">
                        <TrendingUp className="w-4 h-4" />
                        Total Distribuido
                      </div>
                      <div className="text-2xl gotham-bold text-white">
                        {formatCurrency(commissionsSummary.summary?.total_distributed || 0)}
                      </div>
                      <div className="text-white/50 text-xs mt-1">
                        {commissionsSummary.period}
                      </div>
                    </div>
                  </div>

                  {/* Two Column Layout for Top 50 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top 50 Authors - Pool */}
                    <div className="bg-black/30 border border-purple-500/20 rounded-xl p-4">
                      <h4 className="gotham-bold text-white mb-3 flex items-center gap-2">
                        <Award className="w-4 h-4 text-purple-400" />
                        Top 50 Autores - Pool
                      </h4>
                      <div className="max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-black/80">
                            <tr className="border-b border-purple-500/20">
                              <th className="text-left py-2 px-2 text-white/60">#</th>
                              <th className="text-left py-2 px-2 text-white/60">Autor</th>
                              <th className="text-right py-2 px-2 text-white/60">Ganancias</th>
                            </tr>
                          </thead>
                          <tbody>
                            {commissionsSummary.top_authors_pool?.map((author, idx) => (
                              <tr key={author.id} className="border-b border-white/5 hover:bg-white/5">
                                <td className="py-2 px-2 text-white/50">{idx + 1}</td>
                                <td className="py-2 px-2">
                                  <div className="text-white text-xs">{author.name}</div>
                                  <div className="text-white/40 text-xs truncate max-w-32">{author.email}</div>
                                </td>
                                <td className="py-2 px-2 text-right text-purple-400 gotham-bold">
                                  {formatCurrency(author.pool_earnings)}
                                </td>
                              </tr>
                            ))}
                            {(!commissionsSummary.top_authors_pool || commissionsSummary.top_authors_pool.length === 0) && (
                              <tr><td colSpan="3" className="py-4 text-center text-white/50">Sin datos</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Top 50 Referrers - Bonus */}
                    <div className="bg-black/30 border border-yellow-500/20 rounded-xl p-4">
                      <h4 className="gotham-bold text-white mb-3 flex items-center gap-2">
                        <Zap className="w-4 h-4 text-yellow-400" />
                        Top 50 Referidores - Bono
                      </h4>
                      <div className="max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-black/80">
                            <tr className="border-b border-yellow-500/20">
                              <th className="text-left py-2 px-2 text-white/60">#</th>
                              <th className="text-left py-2 px-2 text-white/60">Referidor</th>
                              <th className="text-right py-2 px-2 text-white/60">Ganancias</th>
                            </tr>
                          </thead>
                          <tbody>
                            {commissionsSummary.top_referrers_bonus?.map((ref, idx) => (
                              <tr key={ref.id} className="border-b border-white/5 hover:bg-white/5">
                                <td className="py-2 px-2 text-white/50">{idx + 1}</td>
                                <td className="py-2 px-2">
                                  <div className="text-white text-xs">{ref.name}</div>
                                  <div className="text-white/40 text-xs truncate max-w-32">{ref.email}</div>
                                </td>
                                <td className="py-2 px-2 text-right text-yellow-400 gotham-bold">
                                  {formatCurrency(ref.bonus_earnings)}
                                </td>
                              </tr>
                            ))}
                            {(!commissionsSummary.top_referrers_bonus || commissionsSummary.top_referrers_bonus.length === 0) && (
                              <tr><td colSpan="3" className="py-4 text-center text-white/50">Sin datos</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Monthly Trend */}
                  {commissionsSummary.monthly_trend && commissionsSummary.monthly_trend.length > 0 && (
                    <div className="bg-black/30 border border-white/10 rounded-xl p-4">
                      <h4 className="gotham-bold text-white mb-3">Tendencia Últimos 6 Meses</h4>
                      <div className="grid grid-cols-6 gap-2">
                        {commissionsSummary.monthly_trend.slice().reverse().map((m, idx) => (
                          <div key={m.month} className="text-center p-2 bg-black/30 rounded-lg">
                            <div className="text-white/50 text-xs mb-1">{m.label}</div>
                            <div className="text-yellow-400 text-xs">${m.rapid_bonus}</div>
                            <div className="text-purple-400 text-xs">${m.pool_share}</div>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-center gap-6 mt-2 text-xs">
                        <span className="text-yellow-400">● Bono Rápido</span>
                        <span className="text-purple-400">● Pool</span>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={() => fetchCommissionsSummary(commissionsMonth)}
                    variant="outline"
                    size="sm"
                    className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualizar
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-white/50">
                  No hay datos de comisiones disponibles
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Export Reports Section */}
          <Card className="card-viubux-dark border-green-500/30 mt-6" data-testid="admin-export-section">
            <CardHeader>
              <CardTitle className="gotham-bold text-white flex items-center gap-2">
                <Download className="w-5 h-5 text-green-400" />
                Exportar Reportes CSV
              </CardTitle>
              <CardDescription className="verdana-regular text-white/70">
                Descarga reportes completos en formato CSV
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <button
                  onClick={() => handleExportCSV('users')}
                  className="p-4 bg-black/30 border border-green-500/30 rounded-lg hover:border-green-500 hover:bg-green-500/10 transition-all group"
                  data-testid="export-users-btn"
                >
                  <Users className="w-8 h-8 text-green-400 mb-2 group-hover:scale-110 transition-transform" />
                  <h4 className="text-white gotham-bold">Usuarios</h4>
                  <p className="text-white/50 text-sm">Todos los usuarios registrados</p>
                </button>
                
                <button
                  onClick={() => handleExportCSV('transactions')}
                  className="p-4 bg-black/30 border border-blue-500/30 rounded-lg hover:border-blue-500 hover:bg-blue-500/10 transition-all group"
                  data-testid="export-transactions-btn"
                >
                  <DollarSign className="w-8 h-8 text-blue-400 mb-2 group-hover:scale-110 transition-transform" />
                  <h4 className="text-white gotham-bold">Transacciones</h4>
                  <p className="text-white/50 text-sm">Historial de pagos</p>
                </button>
                
                <button
                  onClick={() => handleExportCSV('commissions')}
                  className="p-4 bg-black/30 border border-purple-500/30 rounded-lg hover:border-purple-500 hover:bg-purple-500/10 transition-all group"
                  data-testid="export-commissions-btn"
                >
                  <TrendingUp className="w-8 h-8 text-purple-400 mb-2 group-hover:scale-110 transition-transform" />
                  <h4 className="text-white gotham-bold">Comisiones</h4>
                  <p className="text-white/50 text-sm">Todas las comisiones generadas</p>
                </button>
              </div>
            </CardContent>
          </Card>
          
          {/* System Actions - Reset Finances (Solo Super Admin) */}
          {user?.role === 'super_admin' && (
            <Card className="card-viubux-dark border-red-500/30 mt-6" data-testid="admin-system-actions">
              <CardHeader>
                <CardTitle className="gotham-bold text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Acciones de Sistema (Super Admin)
                </CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Acciones críticas que afectan todo el sistema
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="text-white gotham-bold flex items-center gap-2">
                        <RotateCcw className="w-4 h-4 text-red-400" />
                        Reiniciar Finanzas del Sistema
                      </h4>
                      <p className="text-white/60 text-sm mt-1">
                        Elimina todas las transacciones, comisiones y reinicia los balances de usuarios a $0.00. 
                        Se crea un backup automático antes de eliminar.
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowResetFinancesModal(true)}
                      className="bg-red-600 hover:bg-red-700 text-white shrink-0"
                      data-testid="reset-finances-btn"
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Reiniciar
                    </Button>
                  </div>
                </div>
                
                {/* Historial de Backups */}
                <div className="mt-4 bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white gotham-bold flex items-center gap-2">
                      <Download className="w-4 h-4 text-blue-400" />
                      Historial de Backups
                    </h4>
                    <Button
                      onClick={() => {
                        setShowBackupsSection(!showBackupsSection);
                        if (!showBackupsSection) fetchFinanceBackups();
                      }}
                      variant="outline"
                      size="sm"
                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                      data-testid="toggle-backups-btn"
                    >
                      {showBackupsSection ? 'Ocultar' : 'Ver Backups'}
                    </Button>
                  </div>
                  
                  {showBackupsSection && (
                    <div className="space-y-3">
                      {loadingBackups ? (
                        <p className="text-white/50 text-sm">Cargando backups...</p>
                      ) : financeBackups.length === 0 ? (
                        <p className="text-white/50 text-sm">No hay backups disponibles. Se crearán automáticamente al reiniciar finanzas.</p>
                      ) : (
                        financeBackups.map((backup) => (
                          <div key={backup.backup_id} className="bg-black/30 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-white/80 text-sm">
                                Backup del {new Date(backup.created_at).toLocaleString('es-MX', { 
                                  year: 'numeric', month: 'short', day: 'numeric', 
                                  hour: '2-digit', minute: '2-digit' 
                                })}
                              </span>
                              <div className="flex gap-2">
                                {backup.files?.map((file) => (
                                  <a
                                    key={file.filename}
                                    href={`${API}/admin/finance-backups/${backup.backup_id}/download/${file.filename}`}
                                    download
                                    className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded hover:bg-blue-500/30 transition-colors"
                                  >
                                    {file.filename.split('_').pop()}
                                  </a>
                                ))}
                              </div>
                            </div>
                            <div className="text-xs text-white/50">
                              <span>Usuarios: {backup.data_counts?.users || 0}</span>
                              <span className="mx-2">•</span>
                              <span>Transacciones: {backup.data_counts?.transactions || 0}</span>
                              <span className="mx-2">•</span>
                              <span>Comisiones: {backup.data_counts?.commissions || 0}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                
                {/* Diagnóstico de Base de Datos */}
                <div className="mt-4 bg-green-500/5 border border-green-500/20 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="text-white gotham-bold flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        Diagnóstico de Base de Datos
                      </h4>
                      <p className="text-white/60 text-sm mt-1">
                        Ver estadísticas y estado de la conexión a MongoDB.
                      </p>
                    </div>
                    <Button
                      onClick={handleGetDbDiagnostic}
                      disabled={loadingDiagnostic}
                      className="bg-green-600 hover:bg-green-700 text-white shrink-0"
                      data-testid="db-diagnostic-btn"
                    >
                      {loadingDiagnostic ? '...' : 'Ver Diagnóstico'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          </>
        )}

        {/* Sección Códigos de Acceso - OCULTA por indicación del admin
        {activeTab === 'access-codes' && (
          <AccessCodeManager />
        )}
        */}

        {activeTab === 'users' && (
          <Card className="card-viubux-dark border-purple-500/20" data-testid="admin-users-table">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="gotham-bold text-white">Todos los Usuarios</CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Lista completa de usuarios registrados con relaciones de referidos
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleExportCSV('users')}
                  variant="outline"
                  className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                  data-testid="export-users-csv"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar CSV
                </Button>
                <Button
                  onClick={() => setShowCreateUserModal(true)}
                  className="btn-viubux"
                  data-testid="create-user-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Agregar Usuario
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Barra de búsqueda y filtros */}
              <div className="mb-6 space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Buscador */}
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                    <input
                      type="text"
                      placeholder="Buscar por nombre, email o código de referido..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-500"
                      data-testid="search-users"
                    />
                  </div>
                  
                  {/* Filtro por tipo */}
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-white/50" />
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="px-3 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                      data-testid="filter-type"
                    >
                      <option value="all">Todos los tipos</option>
                      <option value="author">Autores</option>
                      <option value="consumer">Audio-Lectores</option>
                    </select>
                  </div>
                  
                  {/* Filtro por estado */}
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    data-testid="filter-status"
                  >
                    <option value="all">Todos los estados</option>
                    <option value="active">Activos</option>
                    <option value="inactive">Inactivos</option>
                    <option value="pending">Pendientes</option>
                  </select>
                  
                  {/* Selector de ordenamiento rápido */}
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="w-4 h-4 text-white/50" />
                    <select
                      value={`${sortColumn}-${sortDirection}`}
                      onChange={(e) => {
                        const [col, dir] = e.target.value.split('-');
                        setSortColumn(col);
                        setSortDirection(dir);
                      }}
                      className="px-3 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                      data-testid="sort-select"
                    >
                      <option value="created_at-desc">Más recientes</option>
                      <option value="created_at-asc">Más antiguos</option>
                      <option value="full_name-asc">Nombre A-Z</option>
                      <option value="full_name-desc">Nombre Z-A</option>
                      <option value="email-asc">Email A-Z</option>
                      <option value="email-desc">Email Z-A</option>
                      <option value="direct_referrals_count-desc">Más referidos</option>
                      <option value="direct_referrals_count-asc">Menos referidos</option>
                    </select>
                  </div>
                </div>
                
                {/* Contador y acciones masivas */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="text-white/60 text-sm flex flex-wrap items-center gap-2">
                    <span>Mostrando {filteredUsers.length} de {users.length} usuarios</span>
                    {searchTerm && <span className="text-blue-400">• Búsqueda: "{searchTerm}"</span>}
                    <span className="text-purple-400">
                      • Ordenado por: {
                        sortColumn === 'full_name' ? 'Nombre' :
                        sortColumn === 'email' ? 'Email' :
                        sortColumn === 'username' ? 'Username' :
                        sortColumn === 'user_type' ? 'Tipo' :
                        sortColumn === 'membership_status' ? 'Estado' :
                        sortColumn === 'referral_code' ? 'Código' :
                        sortColumn === 'direct_referrals_count' ? 'Referidos' :
                        sortColumn === 'created_at' ? 'Fecha Registro' : sortColumn
                      } ({sortDirection === 'asc' ? '↑' : '↓'})
                    </span>
                    {selectedUserIds.length > 0 && (
                      <span className="text-yellow-400">• {selectedUserIds.length} seleccionados</span>
                    )}
                  </div>
                  
                  {/* Acciones masivas */}
                  {selectedUserIds.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulkAction('verify_email')}
                        className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                      >
                        <UserCheck className="w-3 h-3 mr-1" />
                        Verificar Email
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulkAction('activate')}
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                      >
                        Activar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulkAction('deactivate')}
                        className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                      >
                        Desactivar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulkAction('change_type', 'author')}
                        className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                      >
                        → Autor
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulkAction('change_type', 'consumer')}
                        className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
                      >
                        → Audio-Lector
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulkAction('delete')}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full verdana-regular text-sm">
                  <thead>
                    <tr className="border-b border-purple-500/20">
                      <th className="text-left py-3 px-2 text-white/80">
                        <button 
                          onClick={toggleSelectAll}
                          className="p-1 hover:bg-purple-500/20 rounded"
                          title={selectedUserIds.length === filteredUsers.length ? "Deseleccionar todos" : "Seleccionar todos"}
                        >
                          {selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0 ? (
                            <CheckSquare className="w-4 h-4 text-purple-400" />
                          ) : (
                            <Square className="w-4 h-4 text-white/50" />
                          )}
                        </button>
                      </th>
                      <SortableHeader column="full_name">Nombre</SortableHeader>
                      <SortableHeader column="username">Username</SortableHeader>
                      <SortableHeader column="email">Email</SortableHeader>
                      <SortableHeader column="user_type">Tipo</SortableHeader>
                      <th className="text-left py-3 px-3 text-white/80">Obras</th>
                      <SortableHeader column="membership_status">Estado</SortableHeader>
                      <SortableHeader column="referral_code">Su Código</SortableHeader>
                      <SortableHeader column="direct_referrals_count">Referidos</SortableHeader>
                      <th className="text-left py-3 px-3 text-white/80">Invitado Por</th>
                      <SortableHeader column="created_at">Registro</SortableHeader>
                      <th className="text-left py-3 px-3 text-white/80">Vencimiento</th>
                      <th className="text-left py-3 px-3 text-white/80">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u, idx) => (
                      <tr key={idx} className={`border-b border-purple-500/10 hover:bg-purple-500/5 ${selectedUserIds.includes(u.id) ? 'bg-purple-500/10' : ''}`} data-testid={`admin-user-row-${idx}`}>
                        <td className="py-3 px-2">
                          <button 
                            onClick={() => toggleSelectUser(u.id)}
                            className="p-1 hover:bg-purple-500/20 rounded"
                          >
                            {selectedUserIds.includes(u.id) ? (
                              <CheckSquare className="w-4 h-4 text-purple-400" />
                            ) : (
                              <Square className="w-4 h-4 text-white/30" />
                            )}
                          </button>
                        </td>
                        <td className="py-3 px-3 text-white">{u.full_name}</td>
                        <td className="py-3 px-3 text-purple-400 text-xs font-mono">{u.username || '-'}</td>
                        <td className="py-3 px-3 text-white/70 text-xs">{u.email}</td>
                        <td className="py-3 px-3">
                          <button
                            onClick={() => handleChangeUserType(u.id, u.user_type === 'author' ? 'consumer' : 'author')}
                            className={`px-2 py-1 rounded-full text-xs cursor-pointer hover:opacity-80 ${
                              u.user_type === 'author'
                                ? 'bg-purple-500/20 text-purple-400'
                                : 'bg-blue-500/20 text-blue-400'
                            }`}
                            title="Clic para cambiar tipo"
                          >
                            {u.user_type === 'author' ? 'Autor' : 'Audio-Lector'}
                          </button>
                        </td>
                        <td className="py-3 px-3">
                          {u.user_type === 'author' ? (
                            <span 
                              className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs cursor-pointer hover:bg-blue-500/30"
                              onClick={() => setViewAsUserId(u.id)}
                              title="Ver obras de este autor"
                            >
                              📚 {u.works_count || 0}
                            </span>
                          ) : (
                            <span className="text-white/30 text-xs">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              u.membership_status === 'active'
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {u.membership_status === 'active' ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <code className="bg-black/30 px-2 py-1 rounded text-purple-300 text-xs" data-testid={`user-code-${idx}`}>
                            {u.referral_code}
                          </code>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className="bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full text-xs" data-testid={`user-referrals-count-${idx}`}>
                            {u.direct_referrals_count || 0}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          {u.referrer_name ? (
                            <div className="flex flex-col" data-testid={`user-referrer-${idx}`}>
                              <span className="text-white text-xs">{u.referrer_name}</span>
                              <code className="text-purple-400 text-xs bg-black/20 px-1 rounded">
                                {u.referrer_code}
                              </code>
                            </div>
                          ) : (
                            <button
                              onClick={() => openAssignReferralModal(u)}
                              className="text-yellow-400 text-xs italic hover:text-yellow-300 underline cursor-pointer"
                              data-testid={`assign-referral-${idx}`}
                            >
                              + Asignar referido
                            </button>
                          )}
                        </td>
                        <td className="py-3 px-3 text-white/70 text-xs">
                          {new Date(u.created_at).toLocaleDateString('es-MX')}
                        </td>
                        <td className="py-3 px-3 text-xs">
                          {u.subscription_end_date ? (
                            <span className={`${
                              new Date(u.subscription_end_date) < new Date() 
                                ? 'text-red-400' 
                                : new Date(u.subscription_end_date) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                                  ? 'text-yellow-400'
                                  : 'text-green-400'
                            }`}>
                              {new Date(u.subscription_end_date).toLocaleDateString('es-MX')}
                            </span>
                          ) : u.membership_status === 'active' && u.subsidized ? (
                            <span className="text-purple-400">∞ Subsidiado</span>
                          ) : (
                            <span className="text-white/40">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              data-testid={`view-as-user-${idx}`}
                              onClick={() => setViewAsUserId(u.id)}
                              size="sm"
                              className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/50 text-xs"
                              title="Ver dashboard como este usuario"
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            {!u.publicala_user_id && (
                              <Button
                                data-testid={`sync-publicala-${idx}`}
                                onClick={() => handleSyncToPublicala(u.id, u.email)}
                                disabled={syncingUserId === u.id}
                                size="sm"
                                className="bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 border border-purple-500/50 text-xs"
                                title="Sincronizar a app.viubux.com"
                              >
                                {syncingUserId === u.id ? '...' : 'Sync'}
                              </Button>
                            )}
                            {u.membership_status !== 'active' && (
                              <Button
                                data-testid={`activate-user-${idx}`}
                                onClick={() => {
                                  setSelectedUser(u);
                                  setShowActivateModal(true);
                                }}
                                size="sm"
                                className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/50 text-xs"
                              >
                                Activar
                              </Button>
                            )}
                            {(u.membership_status === 'pending' || u.membership_status === 'inactive') && (
                              <Button
                                data-testid={`reminder-user-${idx}`}
                                onClick={() => handleSendPaymentReminder(u.id)}
                                size="sm"
                                className="bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/50 text-xs"
                                title="Enviar recordatorio de pago"
                              >
                                <Mail className="w-3 h-3" />
                              </Button>
                            )}
                            {/* Reset Password Button */}
                            <Button
                              data-testid={`reset-pwd-${idx}`}
                              onClick={() => handleResetPassword(u.id, u.email)}
                              size="sm"
                              className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/50 text-xs"
                              title="Resetear contraseña"
                            >
                              <Key className="w-3 h-3" />
                            </Button>
                            {/* Author Tier Toggle (solo para autores) */}
                            {u.user_type === 'author' && (
                              <Button
                                data-testid={`tier-${idx}`}
                                onClick={() => handleChangeAuthorTier(u.id, u.email, u.author_tier || 'premium')}
                                size="sm"
                                className={`text-xs ${
                                  u.author_tier === 'lite' 
                                    ? 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 border border-gray-500/50' 
                                    : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 border border-purple-500/50'
                                }`}
                                title={u.author_tier === 'lite' ? 'Autor Lite - Click para Premium' : 'Autor Premium - Click para Lite'}
                              >
                                {u.author_tier === 'lite' ? 'Lite' : 'Premium'}
                              </Button>
                            )}
                            {/* Editar Perfil del Autor (Admin) */}
                            {u.user_type === 'author' && (
                              <Button
                                data-testid={`edit-profile-${idx}`}
                                onClick={() => handleOpenEditProfile(u)}
                                size="sm"
                                className="bg-violet-500/20 text-violet-300 hover:bg-violet-500/30 border border-violet-500/50 text-xs"
                                title="Editar perfil (portada, avatar, bio) y registrar obra"
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                            )}
                            {u.user_type === 'author' && (
                              <Button
                                data-testid={`merge-user-${idx}`}
                                onClick={() => handleOpenMergeModal(u)}
                                size="sm"
                                className="bg-teal-500/20 text-teal-300 hover:bg-teal-500/30 border border-teal-500/50 text-xs"
                                title="Fusionar esta cuenta en otra (convertir a editor o desactivar)"
                              >
                                <GitMerge className="w-3 h-3" />
                              </Button>
                            )}
                            <Button
                              data-testid={`delete-user-${idx}`}
                              onClick={() => handleOpenDeleteModal(u)}
                              size="sm"
                              className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/50 text-xs"
                            >
                              Borrar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Leyenda de relaciones */}
              <div className="mt-6 p-4 bg-black/20 rounded-lg border border-purple-500/20">
                <h4 className="gotham-medium text-white text-sm mb-3">Guía de Relaciones de Referidos</h4>
                <div className="grid md:grid-cols-2 gap-4 text-xs verdana-regular">
                  <div className="flex items-start gap-2">
                    <span className="text-purple-400">Su Código:</span>
                    <span className="text-white/70">El código único que este usuario comparte para invitar a otros.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-purple-400">Referidos:</span>
                    <span className="text-white/70">Cantidad de personas que este usuario ha invitado directamente.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-purple-400">Invitado Por:</span>
                    <span className="text-white/70">El nombre y código del usuario que lo invitó a la plataforma.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-white/40 italic">Sin referidor:</span>
                    <span className="text-white/70">Usuario que se registró sin usar un código de invitación.</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bank Transfer Proofs Tab */}
        {activeTab === 'proofs' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="gotham-bold text-2xl text-white">Comprobantes de Transferencia</h2>
                <p className="text-white/60 mt-1">Verifica y aprueba pagos por transferencia bancaria</p>
              </div>
              <Button
                onClick={fetchBankProofs}
                disabled={loadingProofs}
                className="bg-white/10 text-white hover:bg-white/20"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingProofs ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="card-viubux-dark border-orange-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Pendientes</p>
                      <p className="text-2xl font-bold text-orange-400">
                        {bankProofs.filter(p => p.status === 'pending_verification').length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-viubux-dark border-green-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Aprobados</p>
                      <p className="text-2xl font-bold text-green-400">
                        {bankProofs.filter(p => p.status === 'approved').length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-viubux-dark border-red-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                      <XCircle className="w-6 h-6 text-red-400" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Rechazados</p>
                      <p className="text-2xl font-bold text-red-400">
                        {bankProofs.filter(p => p.status === 'rejected').length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Proofs List */}
            <Card className="card-viubux-dark border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileImage className="w-5 h-5 text-purple-400" />
                  Lista de Comprobantes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingProofs ? (
                  <div className="text-center py-12">
                    <div className="animate-spin w-8 h-8 border-3 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-white/60">Cargando comprobantes...</p>
                  </div>
                ) : bankProofs.length === 0 ? (
                  <div className="text-center py-12">
                    <FileImage className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/40">No hay comprobantes registrados</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-4 text-white/60 font-medium">Usuario</th>
                          <th className="text-left py-3 px-4 text-white/60 font-medium">Plan</th>
                          <th className="text-right py-3 px-4 text-white/60 font-medium">Monto</th>
                          <th className="text-center py-3 px-4 text-white/60 font-medium">Estado</th>
                          <th className="text-left py-3 px-4 text-white/60 font-medium">Fecha</th>
                          <th className="text-center py-3 px-4 text-white/60 font-medium">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bankProofs.map((proof) => (
                          <tr key={proof.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-4 px-4">
                              <div>
                                <p className="text-white font-medium">{proof.user_name}</p>
                                <p className="text-white/50 text-sm">{proof.user_email}</p>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                proof.user_type === 'author' 
                                  ? 'bg-purple-500/20 text-purple-400' 
                                  : 'bg-blue-500/20 text-blue-400'
                              }`}>
                                {proof.user_type === 'author' ? 'Autor' : 'Audio-Lector'}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-right">
                              <span className="text-green-400 font-bold">${proof.amount?.toLocaleString()} MXN</span>
                            </td>
                            <td className="py-4 px-4 text-center">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                proof.status === 'pending_verification' 
                                  ? 'bg-orange-500/20 text-orange-400' 
                                  : proof.status === 'approved'
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-red-500/20 text-red-400'
                              }`}>
                                {proof.status === 'pending_verification' ? 'Pendiente' : 
                                 proof.status === 'approved' ? 'Aprobado' : 'Rechazado'}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-white/60 text-sm">
                              {new Date(proof.created_at).toLocaleString('es-MX', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td className="py-4 px-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <Button
                                  onClick={() => fetchProofDetail(proof.id)}
                                  size="sm"
                                  className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                                  data-testid={`view-proof-${proof.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {proof.status === 'pending_verification' && (
                                  <>
                                    <Button
                                      onClick={() => handleVerifyProof(proof.id, true)}
                                      size="sm"
                                      className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                                      data-testid={`approve-proof-${proof.id}`}
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      onClick={() => handleVerifyProof(proof.id, false)}
                                      size="sm"
                                      className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                                      data-testid={`reject-proof-${proof.id}`}
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'payouts' && (
          <PayoutManagement />
        )}

        {/* Automation Tab */}
        {activeTab === 'automation' && (
          <div className="space-y-6">
            {/* Action Buttons */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="card-viubux-dark border-purple-500/20">
                <CardHeader>
                  <CardTitle className="gotham-bold text-white flex items-center gap-2">
                    <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    Enviar Recordatorios
                  </CardTitle>
                  <CardDescription className="verdana-regular text-white/70">
                    Envía notificaciones a usuarios con membresía por vencer (próximos 7 días)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={handleSendReminders}
                    disabled={automationLoading}
                    className="w-full btn-viubux"
                    data-testid="send-reminders-btn"
                  >
                    {automationLoading ? 'Enviando...' : 'Enviar Recordatorios de Renovación'}
                  </Button>
                </CardContent>
              </Card>

              <Card className="card-viubux-dark border-purple-500/20">
                <CardHeader>
                  <CardTitle className="gotham-bold text-white flex items-center gap-2">
                    <UserX className="w-5 h-5 text-red-400" />
                    Desactivar Vencidas
                  </CardTitle>
                  <CardDescription className="verdana-regular text-white/70">
                    Desactiva automáticamente las membresías que ya han expirado
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={handleDeactivateExpired}
                    disabled={automationLoading}
                    className="w-full bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/50"
                    data-testid="deactivate-expired-btn"
                  >
                    {automationLoading ? 'Procesando...' : 'Desactivar Membresías Vencidas'}
                  </Button>
                </CardContent>
              </Card>

              <Card className="card-viubux-dark border-orange-500/20">
                <CardHeader>
                  <CardTitle className="gotham-bold text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-orange-400" />
                    Procesar Pendientes (+21 días)
                  </CardTitle>
                  <CardDescription className="verdana-regular text-white/70">
                    Usuarios con status "pending" por más de 21 días pasan a "inactive" y reciben email de recordatorio
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={handleProcessStalePending}
                    disabled={automationLoading}
                    className="w-full bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/50"
                    data-testid="process-pending-btn"
                  >
                    {automationLoading ? 'Procesando...' : 'Procesar Usuarios Pendientes'}
                  </Button>
                </CardContent>
              </Card>

              {/* Verificar Membresías Vencidas (con revocación) */}
              <Card className="card-viubux-dark border-yellow-500/20">
                <CardHeader>
                  <CardTitle className="gotham-bold text-white flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    Verificar Expiradas + Revocar
                  </CardTitle>
                  <CardDescription className="verdana-regular text-white/70">
                    Pausa membresías vencidas y revoca acceso en Publica.la y ViubuxPlay
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    onClick={handleRunExpiredCheck}
                    disabled={runningExpiredCheck}
                    className="w-full bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-500/50"
                    data-testid="run-expired-check-btn"
                  >
                    {runningExpiredCheck ? 'Verificando...' : '🔍 Verificar y Pausar Vencidas'}
                  </Button>
                  
                  {expiredCheckResult && (
                    <div className="mt-3 p-3 bg-gray-800/50 rounded-lg text-sm">
                      <p className="text-white/80">Última ejecución:</p>
                      <p className="text-red-400">• Pausadas: {expiredCheckResult.summary?.paused_count || 0}</p>
                      <p className="text-yellow-400">• En gracia: {expiredCheckResult.summary?.in_grace_period_count || 0}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Corregir Suscripciones Anuales */}
            <Card className="card-viubux-dark border-blue-500/20 mt-6">
              <CardHeader>
                <CardTitle className="gotham-bold text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  Corregir Suscripciones Anuales
                </CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Detecta y corrige suscripciones anuales (~$1,910 MXN) con fecha de expiración incorrecta (30 días en lugar de 365)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    onClick={handleDetectAnnualIssues}
                    disabled={loadingAnnualIssues}
                    className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/50"
                  >
                    {loadingAnnualIssues ? 'Buscando...' : '🔍 Detectar Problemas'}
                  </Button>
                  
                  <Button
                    onClick={() => setShowFixAnnualModal(true)}
                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/50"
                  >
                    ✏️ Corregir Manual
                  </Button>
                </div>

                {annualIssues.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full verdana-regular text-sm">
                      <thead>
                        <tr className="border-b border-blue-500/20">
                          <th className="text-left py-2 px-3 text-white/80">Usuario</th>
                          <th className="text-left py-2 px-3 text-white/80">Monto</th>
                          <th className="text-left py-2 px-3 text-white/80">Expira en</th>
                          <th className="text-left py-2 px-3 text-white/80">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {annualIssues.map((issue, idx) => (
                          <tr key={idx} className="border-b border-blue-500/10">
                            <td className="py-2 px-3">
                              <div className="text-white">{issue.name}</div>
                              <div className="text-white/50 text-xs">{issue.email}</div>
                            </td>
                            <td className="py-2 px-3 text-green-400">${issue.transaction_amount?.toLocaleString()}</td>
                            <td className="py-2 px-3">
                              <span className="text-red-400">{issue.days_until_expiry} días</span>
                              <span className="text-white/50 text-xs"> (debería ser 365)</span>
                            </td>
                            <td className="py-2 px-3">
                              <Button
                                size="sm"
                                onClick={() => handleFixAnnualSubscription(issue.email, issue.transaction_amount)}
                                disabled={fixingAnnual === issue.email}
                                className="bg-green-500/20 text-green-400 hover:bg-green-500/30 text-xs"
                              >
                                {fixingAnnual === issue.email ? '...' : '✓ Corregir'}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Modal Corrección Manual */}
            {showFixAnnualModal && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                <div className="bg-gray-900 p-6 rounded-xl border border-purple-500/30 w-full max-w-md">
                  <h3 className="text-xl font-bold text-white mb-4">Corregir Suscripción Anual</h3>
                  <p className="text-white/70 text-sm mb-4">
                    Ingresa el email del usuario y el monto de su transacción anual para corregir la fecha de expiración a 365 días.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white/80 text-sm mb-1">Email del usuario</label>
                      <input
                        type="email"
                        value={manualFixEmail}
                        onChange={(e) => setManualFixEmail(e.target.value)}
                        placeholder="usuario@ejemplo.com"
                        className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white/80 text-sm mb-1">Monto de transacción (MXN)</label>
                      <input
                        type="number"
                        value={manualFixAmount}
                        onChange={(e) => setManualFixAmount(e.target.value)}
                        placeholder="1910.40"
                        className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                      />
                      <p className="text-white/50 text-xs mt-1">Suscripciones anuales son ~$1,910 MXN</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <Button
                      onClick={() => {
                        setShowFixAnnualModal(false);
                        setManualFixEmail('');
                        setManualFixAmount('');
                      }}
                      className="flex-1 bg-gray-700 text-white hover:bg-gray-600"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={() => {
                        if (manualFixEmail && manualFixAmount) {
                          handleFixAnnualSubscription(manualFixEmail, manualFixAmount);
                          setShowFixAnnualModal(false);
                          setManualFixEmail('');
                          setManualFixAmount('');
                        } else {
                          toast.error('Completa ambos campos');
                        }
                      }}
                      disabled={!manualFixEmail || !manualFixAmount}
                      className="flex-1 bg-green-600 text-white hover:bg-green-700"
                    >
                      Corregir
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Detect Paid but Inactive Users */}
            <Card className="card-viubux-dark border-red-500/20 mt-6">
              <CardHeader>
                <CardTitle className="gotham-bold text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Detectar Pagos sin Activar
                </CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Encuentra usuarios que pagaron pero aparecen como inactivos (inconsistencia de datos)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    onClick={detectPaidButInactive}
                    disabled={loadingPaidInactive}
                    className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/50"
                  >
                    {loadingPaidInactive ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Buscando...</>
                    ) : (
                      '🔍 Detectar Inconsistencias'
                    )}
                  </Button>
                  
                  {paidButInactive.length > 0 && (
                    <Button
                      onClick={fixAllPaidButInactive}
                      disabled={fixingAll}
                      className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/50"
                    >
                      {fixingAll ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Corrigiendo...</>
                      ) : (
                        `✓ Corregir Todos (${paidButInactive.length})`
                      )}
                    </Button>
                  )}
                </div>

                {paidButInactive.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full verdana-regular text-sm">
                      <thead>
                        <tr className="border-b border-red-500/20">
                          <th className="text-left py-2 px-3 text-white/80">Usuario</th>
                          <th className="text-left py-2 px-3 text-white/80">Pago</th>
                          <th className="text-left py-2 px-3 text-white/80">Estado</th>
                          <th className="text-left py-2 px-3 text-white/80">Días Restantes</th>
                          <th className="text-left py-2 px-3 text-white/80">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paidButInactive.map((issue, idx) => (
                          <tr key={idx} className="border-b border-red-500/10">
                            <td className="py-2 px-3">
                              <div className="text-white">{issue.name}</div>
                              <div className="text-white/50 text-xs">{issue.email}</div>
                            </td>
                            <td className="py-2 px-3">
                              <span className="text-green-400 font-semibold">${issue.payment?.amount?.toLocaleString()}</span>
                              <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                                issue.payment?.type === 'annual' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                              }`}>
                                {issue.payment?.type === 'annual' ? 'Anual' : 'Mensual'}
                              </span>
                            </td>
                            <td className="py-2 px-3">
                              <span className="text-red-400">{issue.current_status}</span>
                            </td>
                            <td className="py-2 px-3">
                              <span className="text-yellow-400">{issue.days_remaining} días</span>
                            </td>
                            <td className="py-2 px-3">
                              <Button
                                size="sm"
                                onClick={() => fixPaidButInactive(issue.user_id, issue.email)}
                                disabled={fixingUser === issue.user_id}
                                className="bg-green-500/20 text-green-400 hover:bg-green-500/30 text-xs"
                              >
                                {fixingUser === issue.user_id ? '...' : '✓ Activar'}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expiring Memberships List */}
            <Card className="card-viubux-dark border-purple-500/20">
              <CardHeader>
                <CardTitle className="gotham-bold text-white">
                  Membresías Próximas a Vencer (30 días)
                </CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  {expiringMembers.length} usuarios con membresía por expirar
                </CardDescription>
              </CardHeader>
              <CardContent>
                {expiringMembers.length === 0 ? (
                  <div className="text-center py-8">
                    <UserCheck className="w-12 h-12 mx-auto mb-3 text-green-400" />
                    <p className="verdana-regular text-white/70">
                      No hay membresías por vencer en los próximos 30 días
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full verdana-regular text-sm">
                      <thead>
                        <tr className="border-b border-purple-500/20">
                          <th className="text-left py-3 px-3 text-white/80">Usuario</th>
                          <th className="text-left py-3 px-3 text-white/80">Email</th>
                          <th className="text-left py-3 px-3 text-white/80">Tipo</th>
                          <th className="text-left py-3 px-3 text-white/80">Vence</th>
                          <th className="text-left py-3 px-3 text-white/80">Días Restantes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expiringMembers.map((member, idx) => (
                          <tr key={idx} className="border-b border-purple-500/10 hover:bg-purple-500/5">
                            <td className="py-3 px-3 text-white">{member.full_name}</td>
                            <td className="py-3 px-3 text-white/70 text-xs">{member.email}</td>
                            <td className="py-3 px-3">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                member.user_type === 'author'
                                  ? 'bg-purple-500/20 text-purple-400'
                                  : 'bg-blue-500/20 text-blue-400'
                              }`}>
                                {member.user_type === 'author' ? 'Autor' : 'Audio-Lector'}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-white/70">
                              {new Date(member.expires_at).toLocaleDateString('es-MX')}
                            </td>
                            <td className="py-3 px-3">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                member.days_remaining <= 3
                                  ? 'bg-red-500/20 text-red-400'
                                  : member.days_remaining <= 7
                                    ? 'bg-yellow-500/20 text-yellow-400'
                                    : 'bg-green-500/20 text-green-400'
                              }`}>
                                {member.days_remaining} días
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Tab - Discount Configuration */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Referral Discount Settings */}
            <Card className="card-viubux-dark border-purple-500/20" data-testid="discount-settings-card">
              <CardHeader>
                <CardTitle className="gotham-bold text-white flex items-center gap-2">
                  <Percent className="w-5 h-5" style={{ color: '#10b981' }} />
                  Descuento por Referido de Autor
                </CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Configura el porcentaje y duración del descuento para usuarios referidos por autores
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Current Settings Display */}
                  {discountSettings && !editingDiscount && (
                    <div className="grid md:grid-cols-4 gap-4">
                      <div className="bg-black/30 rounded-lg p-4 text-center">
                        <p className="text-white/60 text-sm mb-1">Descuento</p>
                        <p className="gotham-bold text-3xl text-green-400">{discountSettings.discount_percent}%</p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-4 text-center">
                        <p className="text-white/60 text-sm mb-1">Duración</p>
                        <p className="gotham-bold text-3xl text-blue-400">{discountSettings.discount_months}</p>
                        <p className="text-white/60 text-xs">meses</p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-4 text-center">
                        <p className="text-white/60 text-sm mb-1">Estado</p>
                        <p className={`gotham-bold text-xl ${discountSettings.is_active ? 'text-green-400' : 'text-red-400'}`}>
                          {discountSettings.is_active ? 'Activo' : 'Inactivo'}
                        </p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-4 text-center flex items-center justify-center">
                        <Button
                          onClick={() => setEditingDiscount(true)}
                          className="btn-viubux"
                          data-testid="edit-discount-btn"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Modificar
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Edit Form */}
                  {editingDiscount && (
                    <div className="bg-black/30 rounded-lg p-6 space-y-4">
                      <h4 className="gotham-medium text-white text-lg mb-4">Modificar Configuración</h4>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-white/70 text-sm mb-2">
                            Porcentaje de Descuento (1-100%)
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={newDiscountPercent}
                            onChange={(e) => setNewDiscountPercent(parseInt(e.target.value) || 0)}
                            className="w-full px-4 py-3 bg-black/50 border border-purple-500/30 rounded-lg text-white text-xl text-center focus:outline-none focus:border-purple-500"
                            data-testid="discount-percent-input"
                          />
                        </div>
                        <div>
                          <label className="block text-white/70 text-sm mb-2">
                            Cantidad de Meses (1-24)
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="24"
                            value={newDiscountMonths}
                            onChange={(e) => setNewDiscountMonths(parseInt(e.target.value) || 0)}
                            className="w-full px-4 py-3 bg-black/50 border border-purple-500/30 rounded-lg text-white text-xl text-center focus:outline-none focus:border-purple-500"
                            data-testid="discount-months-input"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <label className="text-white/70">Estado del descuento:</label>
                        <button
                          onClick={() => setDiscountActive(!discountActive)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            discountActive 
                              ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                              : 'bg-red-500/20 text-red-400 border border-red-500/50'
                          }`}
                          data-testid="discount-active-toggle"
                        >
                          {discountActive ? 'Activo' : 'Inactivo'}
                        </button>
                      </div>

                      <div className="flex gap-3 mt-6">
                        <Button
                          onClick={() => {
                            setEditingDiscount(false);
                            setNewDiscountPercent(discountSettings?.discount_percent || 20);
                            setNewDiscountMonths(discountSettings?.discount_months || 3);
                            setDiscountActive(discountSettings?.is_active ?? true);
                          }}
                          variant="outline"
                          className="flex-1 border-purple-500/50 text-white hover:bg-purple-500/20"
                          data-testid="cancel-discount-edit"
                        >
                          Cancelar
                        </Button>
                        <Button
                          onClick={handleSaveDiscountSettings}
                          disabled={savingDiscount || newDiscountPercent < 1 || newDiscountPercent > 100 || newDiscountMonths < 1 || newDiscountMonths > 24}
                          className="flex-1 btn-viubux"
                          data-testid="save-discount-btn"
                        >
                          {savingDiscount ? 'Guardando...' : 'Guardar Cambios'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Info Box */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="gotham-medium text-white mb-2">¿Cómo funciona?</h4>
                    <ul className="verdana-regular text-white/70 text-sm space-y-1">
                      <li>• Cuando un <strong>autor</strong> comparte su enlace de referido, los nuevos usuarios reciben un descuento en su suscripción mensual.</li>
                      <li>• El descuento se aplica automáticamente durante los primeros <strong>{discountSettings?.discount_months || 3} meses</strong> de suscripción.</li>
                      <li>• El precio normal de suscripción es <strong>$199 MXN/mes</strong>.</li>
                      <li>• Con el descuento actual, el precio sería <strong>${(199 * (1 - (discountSettings?.discount_percent || 20) / 100)).toFixed(2)} MXN/mes</strong>.</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Discount Statistics */}
            {discountStats && (
              <Card className="card-viubux-dark border-purple-500/20" data-testid="discount-stats-card">
                <CardHeader>
                  <CardTitle className="gotham-bold text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" style={{ color: '#7012b2' }} />
                    Estadísticas de Descuentos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-black/30 rounded-lg p-4 text-center">
                      <p className="text-white/60 text-sm mb-1">Usuarios con Descuento Activo</p>
                      <p className="gotham-bold text-2xl text-green-400">{discountStats.users_with_active_discount}</p>
                    </div>
                    <div className="bg-black/30 rounded-lg p-4 text-center">
                      <p className="text-white/60 text-sm mb-1">Descuento Agotado</p>
                      <p className="gotham-bold text-2xl text-gray-400">{discountStats.users_discount_exhausted}</p>
                    </div>
                    <div className="bg-black/30 rounded-lg p-4 text-center">
                      <p className="text-white/60 text-sm mb-1">Pagos con Descuento</p>
                      <p className="gotham-bold text-2xl text-blue-400">{discountStats.total_discounted_payments}</p>
                    </div>
                    <div className="bg-black/30 rounded-lg p-4 text-center">
                      <p className="text-white/60 text-sm mb-1">Ahorro Total Usuarios</p>
                      <p className="gotham-bold text-2xl text-yellow-400">{formatCurrency(discountStats.total_amount_saved_by_users)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Free Trial Configuration */}
            <Card className="card-viubux-dark border-cyan-500/20" data-testid="free-trial-card">
              <CardHeader>
                <CardTitle className="gotham-bold text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-cyan-400" />
                  Período de Prueba Gratis
                </CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Configura los días de prueba gratis para nuevos usuarios. Durante el trial, tienen acceso a App.Viubux y Play.Viubux.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingTrialSettings ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Current Settings Display */}
                    <div className="grid md:grid-cols-4 gap-4">
                      <div className="bg-black/30 rounded-lg p-4 text-center">
                        <p className="text-white/60 text-sm mb-1">Estado</p>
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setTrialSettings(prev => ({ ...prev, is_active: !prev.is_active }))}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                              trialSettings.is_active 
                                ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                                : 'bg-red-500/20 text-red-400 border border-red-500/50'
                            }`}
                          >
                            {trialSettings.is_active ? '✓ Activo' : '✗ Inactivo'}
                          </button>
                        </div>
                      </div>
                      <div className="bg-black/30 rounded-lg p-4 text-center">
                        <p className="text-white/60 text-sm mb-1">Días de Prueba</p>
                        <input
                          type="number"
                          min="1"
                          max="30"
                          value={trialSettings.trial_days}
                          onChange={(e) => setTrialSettings(prev => ({ ...prev, trial_days: parseInt(e.target.value) || 14 }))}
                          className="w-20 px-3 py-2 bg-black/50 border border-cyan-500/30 rounded-lg text-white text-2xl text-center focus:outline-none focus:border-cyan-500"
                        />
                      </div>
                      <div className="bg-black/30 rounded-lg p-4 text-center">
                        <p className="text-white/60 text-sm mb-2">Acceso App.Viubux</p>
                        <button
                          onClick={() => setTrialSettings(prev => ({ ...prev, grant_publicala_access: !prev.grant_publicala_access }))}
                          className={`px-3 py-1 rounded text-sm ${
                            trialSettings.grant_publicala_access 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-gray-500/20 text-gray-400'
                          }`}
                        >
                          {trialSettings.grant_publicala_access ? '✓ Incluido' : '✗ No incluido'}
                        </button>
                      </div>
                      <div className="bg-black/30 rounded-lg p-4 text-center">
                        <p className="text-white/60 text-sm mb-2">Acceso Play.Viubux</p>
                        <button
                          onClick={() => setTrialSettings(prev => ({ ...prev, grant_viubuxplay_access: !prev.grant_viubuxplay_access }))}
                          className={`px-3 py-1 rounded text-sm ${
                            trialSettings.grant_viubuxplay_access 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-gray-500/20 text-gray-400'
                          }`}
                        >
                          {trialSettings.grant_viubuxplay_access ? '✓ Incluido' : '✗ No incluido'}
                        </button>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end">
                      <Button
                        onClick={handleSaveTrialSettings}
                        disabled={savingTrialSettings}
                        className="bg-cyan-600 hover:bg-cyan-700 text-white"
                      >
                        {savingTrialSettings ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
                        ) : (
                          <><Check className="w-4 h-4 mr-2" /> Guardar Configuración</>
                        )}
                      </Button>
                    </div>

                    {/* Info Box */}
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                      <h4 className="gotham-medium text-cyan-400 mb-2">ℹ️ Cómo funciona el Trial</h4>
                      <ul className="verdana-regular text-white/70 text-sm space-y-1">
                        <li>• Los nuevos usuarios obtienen <strong>{trialSettings.trial_days} días</strong> de acceso gratuito.</li>
                        <li>• Durante el trial tienen acceso a {trialSettings.grant_publicala_access && trialSettings.grant_viubuxplay_access ? 'App.Viubux y Play.Viubux' : trialSettings.grant_publicala_access ? 'App.Viubux' : 'Play.Viubux'}.</li>
                        <li>• Al terminar el trial, se cobra el <strong>prorrateo</strong> por los días restantes del mes.</li>
                        <li>• El día <strong>1 del siguiente mes</strong>, se cobra la membresía completa ($199 MXN).</li>
                        <li>• Si el usuario tiene descuento por referido, se respeta durante y después del trial.</li>
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Simulator Metrics Visibility */}
            <Card className="card-viubux-dark border-orange-500/20" data-testid="simulator-metrics-card">
              <CardHeader>
                <CardTitle className="gotham-bold text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-orange-400" />
                  Métricas del Simulador
                </CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Controla la visibilidad de las métricas de la comunidad en el simulador de los autores
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingSimulatorSettings ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between bg-black/30 rounded-lg p-4">
                      <div>
                        <p className="text-white font-medium">Mostrar métricas de la comunidad</p>
                        <p className="text-white/50 text-sm mt-1">
                          {simulatorMetricsVisible 
                            ? 'Los autores pueden ver: Audio-Lectores, Autores, Valor Acción Pool, Pool Mensual Total, etc.' 
                            : 'Las métricas están ocultas. Los autores solo verán el simulador de ganancias.'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleSaveSimulatorMetricsSettings(!simulatorMetricsVisible)}
                        disabled={savingSimulatorSettings}
                        className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                          simulatorMetricsVisible 
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50 hover:bg-green-500/30' 
                            : 'bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30'
                        }`}
                      >
                        {savingSimulatorSettings ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : simulatorMetricsVisible ? (
                          <>
                            <Eye className="w-4 h-4" />
                            Visible
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-4 h-4" />
                            Oculto
                          </>
                        )}
                      </button>
                    </div>

                    {/* Info Box */}
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                      <h4 className="gotham-medium text-orange-400 mb-2">⚠️ Información Sensible</h4>
                      <p className="verdana-regular text-white/70 text-sm">
                        Las métricas muestran información financiera como el valor del pool, participación porcentual y cantidad de suscriptores activos. 
                        Oculta esta sección si no deseas que los autores vean estos datos.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Exchange Rate Configuration */}
            <Card className="card-viubux-dark border-purple-500/20" data-testid="exchange-rate-card">
              <CardHeader>
                <CardTitle className="gotham-bold text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  Tipo de Cambio USD → MXN
                </CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Configura el tipo de cambio para convertir las regalías de USD a MXN
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {!editingExchangeRate ? (
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-black/30 rounded-lg p-4 text-center">
                        <p className="text-white/60 text-sm mb-1">Tipo de Cambio Actual</p>
                        <p className="gotham-bold text-3xl text-green-400">${exchangeRate}</p>
                        <p className="text-white/60 text-xs">MXN por USD</p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-4 text-center">
                        <p className="text-white/60 text-sm mb-1">Última Actualización</p>
                        <p className="gotham-medium text-lg text-white">
                          {exchangeRateUpdatedAt 
                            ? new Date(exchangeRateUpdatedAt).toLocaleDateString('es-MX', { 
                                day: '2-digit', 
                                month: 'short', 
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : 'Valor por defecto'}
                        </p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-4 text-center flex items-center justify-center">
                        <Button
                          onClick={() => {
                            setEditingExchangeRate(true);
                            setNewExchangeRate(exchangeRate.toString());
                          }}
                          className="btn-viubux"
                          data-testid="edit-exchange-rate-btn"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Modificar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-black/30 rounded-lg p-6 space-y-4">
                      <h4 className="gotham-medium text-white text-lg mb-4">Modificar Tipo de Cambio</h4>
                      
                      <div>
                        <label className="block text-white/70 text-sm mb-2">
                          Nuevo Tipo de Cambio (MXN por USD)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="1"
                          max="100"
                          value={newExchangeRate}
                          onChange={(e) => setNewExchangeRate(e.target.value)}
                          placeholder="Ej: 17.50"
                          className="w-full px-4 py-3 bg-black/30 border border-purple-500/30 rounded-lg text-white text-xl text-center gotham-bold focus:outline-none focus:border-purple-500"
                          data-testid="exchange-rate-input"
                        />
                        <p className="text-white/50 text-xs mt-2 text-center">
                          Ejemplo: Si ingresas 17.5, entonces $1 USD = $17.50 MXN
                        </p>
                      </div>

                      <div className="flex gap-3">
                        <Button
                          onClick={() => {
                            setEditingExchangeRate(false);
                            setNewExchangeRate(exchangeRate.toString());
                          }}
                          variant="outline"
                          className="flex-1 border-purple-500/50 text-white hover:bg-purple-500/20"
                          data-testid="cancel-exchange-rate-edit"
                        >
                          Cancelar
                        </Button>
                        <Button
                          onClick={handleSaveExchangeRate}
                          disabled={savingExchangeRate || !newExchangeRate || parseFloat(newExchangeRate) <= 0}
                          className="flex-1 btn-viubux"
                          data-testid="save-exchange-rate-btn"
                        >
                          {savingExchangeRate ? 'Guardando...' : 'Guardar Cambios'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Info Box */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="gotham-medium text-white mb-2">¿Para qué sirve?</h4>
                    <ul className="verdana-regular text-white/70 text-sm space-y-1">
                      <li>• Este tipo de cambio se usa para convertir las regalías de <strong>App.Viubux</strong> de USD a MXN.</li>
                      <li>• Afecta el cálculo de: <strong>Reportes de Regalías</strong> y <strong>Ventas Pendientes</strong>.</li>
                      <li>• El valor actual es <strong>${exchangeRate} MXN</strong> por cada <strong>$1 USD</strong>.</li>
                    </ul>
                  </div>

                  {/* Formula explanation */}
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <h4 className="gotham-medium text-white mb-2">Fórmula de Cálculo de Regalías</h4>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div className="bg-black/20 rounded-lg p-3">
                        <p className="text-green-400 font-bold mb-1">VIUBUX</p>
                        <p className="text-white/70">Ingreso - 33% plataforma - 15% distribuidor → MXN</p>
                      </div>
                      <div className="bg-black/20 rounded-lg p-3">
                        <p className="text-blue-400 font-bold mb-1">OTRAS TIENDAS</p>
                        <p className="text-white/70">Ingreso - 10% retenciones int. - 15% distribuidor → MXN</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Blog/Noticias Tab */}
        {activeTab === 'blog' && (
          <BlogAdmin token={token} />
        )}

        {/* Author Verification Tab */}
        {activeTab === 'verification' && (
          <AuthorVerificationAdmin token={token} />
        )}

        {/* Royalties Tab */}
        {activeTab === 'royalties' && (
          <AdminRoyalties token={token} />
        )}

        {/* Reconciliation Tab */}
        {activeTab === 'reconciliation' && (
          <RoyaltyReconciliation />
        )}

        {/* Play Sync Tab */}
        {activeTab === 'play_sync' && (
          <PlaySyncPanel />
        )}

        {/* Tools Tab */}
        {activeTab === 'tools' && (
          <ImgToolPanel />
        )}

        {/* Activation Modal */}
        {showActivateModal && selectedUser && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="activate-modal">
            <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-md w-full p-6">
              <h3 className="gotham-bold text-2xl text-white mb-4">Activar Membresía</h3>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between verdana-regular">
                  <span className="text-white/70">Usuario:</span>
                  <span className="text-white">{selectedUser.full_name}</span>
                </div>
                <div className="flex justify-between verdana-regular">
                  <span className="text-white/70">Email:</span>
                  <span className="text-white">{selectedUser.email}</span>
                </div>
                <div className="flex justify-between verdana-regular">
                  <span className="text-white/70">Tipo:</span>
                  <span className="text-white capitalize">{selectedUser.user_type === 'author' ? 'Autor' : 'Audio-Lector'}</span>
                </div>
                <div className="flex justify-between verdana-regular">
                  <span className="text-white/70">Monto:</span>
                  <span className="text-white gotham-bold">
                    ${selectedUser.user_type === 'author' ? '4,997' : '199'} MXN
                  </span>
                </div>
              </div>

              {/* Opción 1: Pago en efectivo */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
                <p className="verdana-regular text-yellow-200 text-sm">
                  💵 <strong>Pago en Efectivo:</strong> Registra el pago y genera comisiones para la red de referidos.
                </p>
              </div>

              <div className="flex gap-3 mb-4">
                <Button
                  onClick={() => {
                    setShowActivateModal(false);
                    setSelectedUser(null);
                    setSubsidyType('30_days');
                    setSubsidyNotes('');
                    setMpPaymentId('');
                    setMpVerificationResult(null);
                  }}
                  variant="outline"
                  className="flex-1 border-purple-500/50 text-white hover:bg-purple-500/20"
                  data-testid="cancel-activate"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => handleActivateMembership(selectedUser.id, selectedUser.user_type)}
                  className="flex-1 btn-viubux"
                  data-testid="confirm-activate"
                >
                  💵 Registrar Pago
                </Button>
              </div>

              {/* Opción 1.5: Verificar pago de MercadoPago */}
              <div className="border-t border-white/10 my-4"></div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                <p className="verdana-regular text-blue-200 text-sm mb-3">
                  💳 <strong>Verificar Pago MercadoPago:</strong> Si el webhook no llegó pero el pago existe en MercadoPago.
                </p>
                
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={mpPaymentId}
                    onChange={(e) => setMpPaymentId(e.target.value)}
                    placeholder="ID de pago de MercadoPago (ej: 12345678901)"
                    className="flex-1 px-3 py-2 bg-black/50 border border-blue-500/30 rounded-lg text-white text-sm placeholder-white/40"
                    data-testid="mp-payment-id-input"
                  />
                  <Button
                    onClick={handleVerifyMpPayment}
                    disabled={verifyingMpPayment || !mpPaymentId.trim()}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                    data-testid="verify-mp-btn"
                  >
                    {verifyingMpPayment ? '...' : 'Verificar'}
                  </Button>
                </div>

                {mpVerificationResult && !mpVerificationResult.error && (
                  <div className="bg-black/30 rounded-lg p-3 mb-3 text-xs">
                    <div className="flex justify-between mb-1">
                      <span className="text-white/60">Estado:</span>
                      <span className={mpVerificationResult.status === 'approved' ? 'text-green-400 font-bold' : 'text-yellow-400'}>
                        {mpVerificationResult.status?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex justify-between mb-1">
                      <span className="text-white/60">Monto:</span>
                      <span className="text-white">${mpVerificationResult.amount} {mpVerificationResult.currency}</span>
                    </div>
                    <div className="flex justify-between mb-1">
                      <span className="text-white/60">Email pagador:</span>
                      <span className="text-white">{mpVerificationResult.payer_email || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Fecha:</span>
                      <span className="text-white">{mpVerificationResult.date_approved ? new Date(mpVerificationResult.date_approved).toLocaleDateString('es-MX') : 'N/A'}</span>
                    </div>
                  </div>
                )}

                {mpVerificationResult?.error && (
                  <div className="bg-red-500/20 rounded-lg p-2 mb-3">
                    <p className="text-red-400 text-xs">{mpVerificationResult.message}</p>
                  </div>
                )}

                {mpVerificationResult?.status === 'approved' && (
                  <Button
                    onClick={() => handleActivateWithMpPayment(selectedUser.id)}
                    className="w-full bg-green-500 hover:bg-green-600 text-white"
                    data-testid="activate-with-mp-btn"
                  >
                    ✅ Activar con este pago verificado
                  </Button>
                )}
              </div>
              <div className="border-t border-white/10 my-4"></div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                <p className="verdana-regular text-green-200 text-sm mb-3">
                  🎁 <strong>Subsidiar Inscripción:</strong> Activa la membresía como cortesía. NO genera ingresos ni comisiones.
                </p>
                
                {/* Tipo de subsidio */}
                <div className="space-y-2 mb-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="subsidyType"
                      value="30_days"
                      checked={subsidyType === '30_days'}
                      onChange={(e) => setSubsidyType(e.target.value)}
                      className="w-4 h-4 text-green-500"
                    />
                    <span className="text-white text-sm">
                      <strong>30 días</strong> - Membresía temporal (expira en 1 mes)
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="subsidyType"
                      value="indefinite"
                      checked={subsidyType === 'indefinite'}
                      onChange={(e) => setSubsidyType(e.target.value)}
                      className="w-4 h-4 text-green-500"
                    />
                    <span className="text-white text-sm">
                      <strong>Indefinido</strong> - Membresía permanente (no expira)
                    </span>
                  </label>
                </div>

                {/* Notas opcionales */}
                <input
                  type="text"
                  value={subsidyNotes}
                  onChange={(e) => setSubsidyNotes(e.target.value)}
                  placeholder="Notas (opcional) - Ej: Cortesía por evento X"
                  className="w-full bg-black/30 border border-green-500/30 rounded-lg px-3 py-2 text-white text-sm placeholder-white/40"
                />
              </div>
              <Button
                onClick={() => handleSubsidizeInscription(selectedUser.id)}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                data-testid="subsidize-inscription"
              >
                🎁 Subsidiar {subsidyType === 'indefinite' ? 'Indefinidamente' : 'por 30 Días'}
              </Button>
            </div>
          </div>
        )}

        {/* Assign Referral Modal */}
        {showAssignReferralModal && userToAssignReferral && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="assign-referral-modal">
            <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-md w-full p-6">
              <h3 className="gotham-bold text-2xl text-white mb-4 flex items-center gap-2">
                <Users className="w-6 h-6 text-purple-400" />
                Asignar Referido
              </h3>
              
              <div className="space-y-3 mb-4">
                <div className="flex justify-between verdana-regular">
                  <span className="text-white/70">Usuario:</span>
                  <span className="text-white">{userToAssignReferral.full_name}</span>
                </div>
                <div className="flex justify-between verdana-regular">
                  <span className="text-white/70">Email:</span>
                  <span className="text-white text-sm">{userToAssignReferral.email}</span>
                </div>
                <div className="flex justify-between verdana-regular">
                  <span className="text-white/70">Tipo:</span>
                  <span className="text-white capitalize">{userToAssignReferral.user_type === 'author' ? 'Autor' : 'Audio-Lector'}</span>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-white/70 text-sm mb-2">
                  Código de referido del sponsor:
                </label>
                <input
                  type="text"
                  value={referralCodeToAssign}
                  onChange={(e) => setReferralCodeToAssign(e.target.value)}
                  placeholder="Ej: yvBDGQgHGHc"
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-purple-500"
                  data-testid="referral-code-input"
                />
                <p className="text-white/50 text-xs mt-2">
                  Ingresa el código de referido del usuario que será el sponsor/patrocinador
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowAssignReferralModal(false);
                    setUserToAssignReferral(null);
                    setReferralCodeToAssign('');
                  }}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleAssignReferral}
                  disabled={assigningReferral || !referralCodeToAssign.trim()}
                  className="flex-1 bg-purple-500 hover:bg-purple-600 text-white"
                  data-testid="confirm-assign-referral"
                >
                  {assigningReferral ? 'Asignando...' : 'Asignar Referido'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Modal */}
        {showDeleteModal && userToDelete && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="delete-user-modal">
            <div className="bg-gray-900 border border-red-500/30 rounded-2xl max-w-md w-full p-6">
              <h3 className="gotham-bold text-2xl text-white mb-4 flex items-center gap-2">
                <UserX className="w-6 h-6 text-red-400" />
                Borrar Usuario
              </h3>
              
              <div className="space-y-3 mb-4">
                <div className="flex justify-between verdana-regular">
                  <span className="text-white/70">Usuario:</span>
                  <span className="text-white">{userToDelete.full_name}</span>
                </div>
                <div className="flex justify-between verdana-regular">
                  <span className="text-white/70">Email:</span>
                  <span className="text-white text-sm">{userToDelete.email}</span>
                </div>
                <div className="flex justify-between verdana-regular">
                  <span className="text-white/70">Tipo:</span>
                  <span className="text-white capitalize">{userToDelete.user_type === 'author' ? 'Autor' : 'Audio-Lector'}</span>
                </div>
              </div>

              {/* Preview de datos a borrar */}
              {deletePreview && (
                <div className="bg-black/30 rounded-lg p-4 mb-4 space-y-2 text-sm">
                  <p className="text-white/80 font-semibold mb-2">Datos que se eliminarán:</p>
                  <div className="grid grid-cols-2 gap-2 text-white/70">
                    <span>Transacciones:</span>
                    <span className="text-white">{deletePreview.data_to_delete?.transactions || 0}</span>
                    <span>Comisiones:</span>
                    <span className="text-white">{deletePreview.data_to_delete?.commissions_earned || 0}</span>
                    <span>Notificaciones:</span>
                    <span className="text-white">{deletePreview.data_to_delete?.notifications || 0}</span>
                    <span>Referidos afectados:</span>
                    <span className="text-white">{deletePreview.data_to_delete?.referrals_affected || 0}</span>
                  </div>
                </div>
              )}

              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-6">
                <p className="verdana-regular text-red-200 text-sm">
                  ⚠️ <strong>Esta acción es IRREVERSIBLE.</strong> Se borrarán permanentemente todos los datos del usuario, incluyendo transacciones, comisiones y notificaciones.
                </p>
              </div>

              {/* PIN de confirmación */}
              <div className="mb-6">
                <label className="block text-white/70 text-sm mb-2">
                  Ingresa el PIN de 4 dígitos para confirmar:
                </label>
                <input
                  type="password"
                  maxLength={4}
                  value={deletePin}
                  onChange={(e) => setDeletePin(e.target.value.replace(/\D/g, ''))}
                  placeholder="****"
                  className="w-full px-4 py-3 bg-black/30 border border-red-500/30 rounded-lg text-white text-center text-2xl tracking-widest focus:outline-none focus:border-red-500"
                  data-testid="delete-pin-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                    setDeletePin('');
                    setDeletePreview(null);
                  }}
                  variant="outline"
                  className="flex-1 border-purple-500/50 text-white hover:bg-purple-500/20"
                  data-testid="cancel-delete"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleDeleteUser}
                  disabled={deletePin.length !== 4 || deleteLoading}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                  data-testid="confirm-delete"
                >
                  {deleteLoading ? 'Borrando...' : 'Confirmar Borrado'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Reset Finances Modal */}
        {showResetFinancesModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="reset-finances-modal">
            <div className="bg-gray-900 border-2 border-red-500/50 rounded-2xl max-w-lg w-full p-6 shadow-2xl shadow-red-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="gotham-bold text-2xl text-white">Reiniciar Finanzas</h3>
                  <p className="text-red-400 text-sm">Acción DESTRUCTIVA e IRREVERSIBLE</p>
                </div>
              </div>
              
              {/* Advertencia Severa */}
              <div className="bg-red-500/10 border-2 border-red-500/40 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
                  <div className="space-y-2">
                    <p className="text-red-200 font-bold">
                      ¡ADVERTENCIA CRÍTICA!
                    </p>
                    <ul className="text-red-200/90 text-sm space-y-1 list-disc list-inside">
                      <li>Se eliminarán TODAS las transacciones de pago</li>
                      <li>Se eliminarán TODAS las comisiones generadas</li>
                      <li>Se eliminarán TODOS los historiales de payout</li>
                      <li>Se reiniciarán TODOS los balances de usuarios a $0.00</li>
                      <li>Se desactivarán membresías activas (excepto subsidiados)</li>
                      <li>Se eliminarán TODOS los reportes de regalías de App.Viubux</li>
                      <li>Esta acción NO SE PUEDE DESHACER</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Opciones de Reset */}
              <div className="bg-black/30 rounded-lg p-4 mb-6 space-y-3">
                <p className="text-white/80 font-semibold text-sm mb-2">¿Qué deseas reiniciar?</p>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={resetFinancesOptions.reset_transactions}
                    onChange={(e) => setResetFinancesOptions(prev => ({...prev, reset_transactions: e.target.checked}))}
                    className="w-4 h-4 accent-red-500"
                  />
                  <span className="text-white/80 group-hover:text-white">Transacciones de pago</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={resetFinancesOptions.reset_commissions}
                    onChange={(e) => setResetFinancesOptions(prev => ({...prev, reset_commissions: e.target.checked}))}
                    className="w-4 h-4 accent-red-500"
                  />
                  <span className="text-white/80 group-hover:text-white">Comisiones y payouts</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={resetFinancesOptions.reset_user_balances}
                    onChange={(e) => setResetFinancesOptions(prev => ({...prev, reset_user_balances: e.target.checked}))}
                    className="w-4 h-4 accent-red-500"
                  />
                  <span className="text-white/80 group-hover:text-white">Balances de usuarios (poner en $0)</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={resetFinancesOptions.reset_memberships}
                    onChange={(e) => setResetFinancesOptions(prev => ({...prev, reset_memberships: e.target.checked}))}
                    className="w-4 h-4 accent-red-500"
                  />
                  <span className="text-white/80 group-hover:text-white">Membresías activas (excepto subsidiados y super admin)</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={resetFinancesOptions.reset_royalty_reports}
                    onChange={(e) => setResetFinancesOptions(prev => ({...prev, reset_royalty_reports: e.target.checked}))}
                    className="w-4 h-4 accent-red-500"
                  />
                  <span className="text-white/80 group-hover:text-white">Reportes de regalías (App.Viubux)</span>
                </label>
              </div>

              {/* Confirmación por texto */}
              <div className="mb-4">
                <label className="block text-white/70 text-sm mb-2">
                  Escribe <span className="text-red-400 font-bold">REINICIAR</span> para confirmar:
                </label>
                <input
                  type="text"
                  value={resetFinancesConfirmText}
                  onChange={(e) => setResetFinancesConfirmText(e.target.value.toUpperCase())}
                  placeholder="REINICIAR"
                  className="w-full px-4 py-3 bg-black/30 border border-red-500/30 rounded-lg text-white text-center font-bold tracking-widest focus:outline-none focus:border-red-500"
                  data-testid="reset-confirm-text-input"
                />
              </div>

              {/* PIN de confirmación */}
              <div className="mb-6">
                <label className="block text-white/70 text-sm mb-2">
                  Ingresa el PIN de 4 dígitos para confirmar:
                </label>
                <input
                  type="password"
                  maxLength={4}
                  value={resetFinancesPin}
                  onChange={(e) => setResetFinancesPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="****"
                  className="w-full px-4 py-3 bg-black/30 border border-red-500/30 rounded-lg text-white text-center text-2xl tracking-widest focus:outline-none focus:border-red-500"
                  data-testid="reset-pin-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowResetFinancesModal(false);
                    setResetFinancesPin('');
                    setResetFinancesConfirmText('');
                    setResetFinancesOptions({
                      reset_transactions: true,
                      reset_commissions: true,
                      reset_user_balances: true
                    });
                  }}
                  variant="outline"
                  className="flex-1 border-gray-500/50 text-white hover:bg-gray-500/20"
                  data-testid="cancel-reset-finances"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleResetFinances}
                  disabled={
                    resetFinancesPin.length !== 4 || 
                    resetFinancesConfirmText !== 'REINICIAR' || 
                    resetFinancesLoading ||
                    (!resetFinancesOptions.reset_transactions && !resetFinancesOptions.reset_commissions && !resetFinancesOptions.reset_user_balances)
                  }
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="confirm-reset-finances"
                >
                  {resetFinancesLoading ? (
                    <span className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Procesando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Reiniciar Todo
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <Card className="card-viubux-dark border-purple-500/20" data-testid="admin-transactions-table">
            <CardHeader>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                  <CardTitle className="gotham-bold text-white flex items-center gap-2">
                    Todas las Transacciones
                    {selectedTransactionIds.length > 0 && (
                      <span className="text-yellow-400 text-sm font-normal">• {selectedTransactionIds.length} seleccionadas</span>
                    )}
                  </CardTitle>
                  <CardDescription className="verdana-regular text-white/70">
                    Historial completo de pagos y regalías
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  {/* Acciones masivas de transacciones */}
                  {selectedTransactionIds.length > 0 && (
                    <div className="flex flex-wrap gap-2 mr-4 p-2 bg-purple-500/10 rounded-lg border border-purple-500/30">
                      <Button
                        size="sm"
                        onClick={() => handleBulkTransactionAction('mark_paid')}
                        className="bg-green-600 hover:bg-green-500 text-white text-xs"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Marcar Pagadas
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleBulkTransactionAction('mark_pending')}
                        className="bg-yellow-600 hover:bg-yellow-500 text-white text-xs"
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        Marcar Pendientes
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleBulkTransactionAction('cancel')}
                        className="bg-orange-600 hover:bg-orange-500 text-white text-xs"
                      >
                        <XCircle className="w-3 h-3 mr-1" />
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleBulkTransactionAction('delete')}
                        className="bg-red-600 hover:bg-red-500 text-white text-xs"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  )}
                  <select
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'all') {
                        fetchAdminData();
                      } else if (value === 'royalties') {
                        setTransactions(transactions.filter(t => t.transaction_type === 'royalty'));
                      } else if (value === 'pool') {
                        setTransactions(transactions.filter(t => t.transaction_type === 'pool_contribution'));
                      } else {
                        setTransactions(transactions.filter(t => t.transaction_type === 'payment'));
                      }
                      setSelectedTransactionIds([]);
                    }}
                    className="bg-black/50 border border-purple-500/30 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    <option value="all">Todos</option>
                    <option value="payments">Solo Pagos</option>
                    <option value="royalties">Solo Regalías</option>
                    <option value="pool">Solo Pool Autores</option>
                  </select>
                  <Button
                    onClick={() => handleExportCSV('transactions')}
                    variant="outline"
                    className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                    data-testid="export-transactions-csv"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Exportar CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full verdana-regular">
                  <thead>
                    <tr className="border-b border-purple-500/20">
                      <th className="text-left py-3 px-2 text-white/80">
                        <button 
                          onClick={toggleSelectAllTransactions}
                          className="p-1 hover:bg-purple-500/20 rounded"
                          title={selectedTransactionIds.length === transactions.length ? "Deseleccionar todas" : "Seleccionar todas"}
                        >
                          {selectedTransactionIds.length === transactions.length && transactions.length > 0 ? (
                            <CheckSquare className="w-4 h-4 text-purple-400" />
                          ) : (
                            <Square className="w-4 h-4 text-white/50" />
                          )}
                        </button>
                      </th>
                      <th className="text-left py-3 px-4 text-white/80">Tipo</th>
                      <th className="text-left py-3 px-4 text-white/80">ID</th>
                      <th className="text-left py-3 px-4 text-white/80">Usuario</th>
                      <th className="text-left py-3 px-4 text-white/80">Descripción</th>
                      <th className="text-left py-3 px-4 text-white/80">Monto</th>
                      <th className="text-left py-3 px-4 text-white/80">Estado</th>
                      <th className="text-left py-3 px-4 text-white/80">Fecha</th>
                      <th className="text-left py-3 px-4 text-white/80">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(!transactions || transactions.length === 0) ? (
                      <tr>
                        <td colSpan="9" className="py-8 text-center text-white/50">
                          No hay transacciones registradas
                        </td>
                      </tr>
                    ) : transactions.map((t, idx) => {
                      const txId = t.id || t.session_id;
                      const isSelected = selectedTransactionIds.includes(txId);
                      return (
                        <tr 
                          key={idx} 
                          className={`border-b border-purple-500/10 hover:bg-purple-500/5 ${
                            t.transaction_type === 'royalty' ? 'bg-yellow-500/5' : 
                            t.transaction_type === 'pool_contribution' ? 'bg-blue-500/5' : ''
                          } ${isSelected ? 'bg-purple-500/15' : ''}`} 
                          data-testid={`admin-transaction-row-${idx}`}
                        >
                          <td className="py-3 px-2">
                            <button
                              onClick={() => toggleTransactionSelection(txId)}
                              className="p-1 hover:bg-purple-500/20 rounded"
                            >
                              {isSelected ? (
                                <CheckSquare className="w-4 h-4 text-purple-400" />
                              ) : (
                                <Square className="w-4 h-4 text-white/30" />
                              )}
                            </button>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              t.transaction_type === 'royalty' 
                                ? 'bg-yellow-500/20 text-yellow-400' 
                                : t.transaction_type === 'pool_contribution'
                                ? 'bg-blue-500/20 text-blue-400'
                                : 'bg-green-500/20 text-green-400'
                            }`}>
                              {t.transaction_type === 'royalty' ? '📚 Regalía' : 
                               t.transaction_type === 'pool_contribution' ? '👥 Pool Autores' : '💳 Pago'}
                            </span>
                          </td>
                        <td className="py-3 px-4 text-white/70 font-mono text-xs">
                          {(t.id || t.session_id || t.external_reference || 'N/A').substring(0, 12)}...
                        </td>
                        <td className="py-3 px-4 text-white">
                          <div>
                            <div className="font-medium">{t.user_name || 'N/A'}</div>
                            <div className="text-xs text-white/50">{t.user_email || ''}</div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-white/70 text-sm">
                          {t.transaction_type === 'royalty' 
                            ? `${t.description || `Período: ${t.period_start} - ${t.period_end}`}` 
                            : t.transaction_type === 'pool_contribution'
                            ? (
                              <div>
                                <div>{t.description}</div>
                                {t.authors_count > 0 && (
                                  <div className="text-xs text-blue-400">{t.authors_count} autores • ${t.total_distributed?.toFixed(2) || '0.00'} distribuido</div>
                                )}
                              </div>
                            )
                            : (t.payment_type || t.payment_provider || 'Pago')}
                          {t.total_units > 0 && (
                            <div className="text-xs text-purple-400">{t.total_units} unidades</div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-white gotham-bold">
                          {formatCurrency(t.amount || 0)} {(t.currency || 'MXN').toUpperCase()}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              t.payment_status === 'paid' || t.payment_status === 'distributed'
                                ? 'bg-green-500/20 text-green-400'
                                : t.payment_status === 'pending'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : t.payment_status === 'approved'
                                ? 'bg-blue-500/20 text-blue-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {t.payment_status === 'paid' ? 'Pagado' : 
                             t.payment_status === 'distributed' ? 'Distribuido' :
                             t.payment_status === 'pending' ? 'Pendiente' : 
                             t.payment_status === 'approved' ? 'Aprobado' : 
                             t.payment_status || 'Desconocido'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-white/70">
                          {t.created_at ? new Date(t.created_at).toLocaleDateString('es-MX') : 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            {t.transaction_type !== 'royalty' ? (
                              <>
                                <Button
                                  onClick={() => openUpdateTxModal(t)}
                                  size="sm"
                                  variant="outline"
                                  className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20 text-xs px-2"
                                  data-testid={`update-tx-${idx}`}
                                  title="Editar"
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button
                                  onClick={() => handleRefundTransaction(t)}
                                  size="sm"
                                  variant="outline"
                                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20 text-xs px-2"
                                  title="Devolución"
                                >
                                  <RotateCcw className="w-3 h-3" />
                                </Button>
                                <Button
                                  onClick={() => handleDeleteTransaction(t)}
                                  size="sm"
                                  variant="outline"
                                  className="border-red-500/50 text-red-400 hover:bg-red-500/20 text-xs px-2"
                                  title="Eliminar"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  onClick={() => setActiveTab('royalties')}
                                  size="sm"
                                  variant="outline"
                                  className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20 text-xs px-2"
                                  title="Ver Regalías"
                                  data-testid={`view-royalty-${idx}`}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  Ver Regalías
                                </Button>
                                <Button
                                  onClick={() => handleEditRoyaltyReport(t)}
                                  size="sm"
                                  variant="outline"
                                  className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20 text-xs px-2"
                                  title="Editar"
                                  data-testid={`edit-royalty-${idx}`}
                                >
                                  <Edit className="w-3 h-3 mr-1" />
                                  Editar
                                </Button>
                                <Button
                                  onClick={() => handleDeleteRoyaltyReport(t)}
                                  size="sm"
                                  variant="outline"
                                  className="border-red-500/50 text-red-400 hover:bg-red-500/20 text-xs px-2"
                                  title="Eliminar"
                                  data-testid={`delete-royalty-${idx}`}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Eliminar
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Publica.la Sync Tab */}
        {activeTab === 'publicala' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="card-viubux-dark border-cyan-500/30 bg-gradient-to-br from-cyan-900/10 to-transparent">
                <CardHeader className="pb-2">
                  <CardTitle className="gotham-medium text-white text-sm flex items-center gap-2">
                    <Users className="w-4 h-4 text-cyan-400" />
                    Usuarios Sincronizados
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="gotham-black text-3xl text-cyan-400">
                    {publicalaLoading ? '...' : publicalaStats?.total_synced_users || 0}
                  </div>
                  <div className="text-white/50 text-xs mt-1">
                    {publicalaStats?.active_synced_users || 0} activos
                  </div>
                </CardContent>
              </Card>
              
              <Card className="card-viubux-dark border-purple-500/30 bg-gradient-to-br from-purple-900/10 to-transparent">
                <CardHeader className="pb-2">
                  <CardTitle className="gotham-medium text-white text-sm flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-purple-400" />
                    Usuarios en Trial
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="gotham-black text-3xl text-purple-400">
                    {publicalaLoading ? '...' : publicalaStats?.trial_users || 0}
                  </div>
                  <div className="text-white/50 text-xs mt-1">
                    15 días de acceso gratuito
                  </div>
                </CardContent>
              </Card>
              
              <Card className="card-viubux-dark border-blue-500/30 bg-gradient-to-br from-blue-900/10 to-transparent">
                <CardHeader className="pb-2">
                  <CardTitle className="gotham-medium text-white text-sm flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-blue-400" />
                    Eventos de Sync
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="gotham-black text-3xl text-blue-400">
                    {publicalaLoading ? '...' : publicalaStats?.total_sync_events || 0}
                  </div>
                  <div className="text-white/50 text-xs mt-1">
                    {publicalaStats?.events_last_24h || 0} últimas 24h
                  </div>
                </CardContent>
              </Card>
              
              <Card className="card-viubux-dark border-green-500/30 bg-gradient-to-br from-green-900/10 to-transparent">
                <CardHeader className="pb-2">
                  <CardTitle className="gotham-medium text-white text-sm flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-400" />
                    Ingresos Sincronizados
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="gotham-black text-3xl text-green-400">
                    {publicalaLoading ? '...' : formatCurrency(publicalaStats?.total_revenue_mxn || 0)}
                  </div>
                  <div className="text-white/50 text-xs mt-1">
                    MXN (desde app.viubux.com)
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Webhook Info Card */}
            <Card className="card-viubux-dark border-cyan-500/20">
              <CardHeader>
                <CardTitle className="gotham-bold text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-cyan-400" />
                  Configuración del Webhook
                </CardTitle>
                <CardDescription className="text-white/60">
                  Configura este endpoint en App.Viubux para recibir notificaciones de compras
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-black/40 rounded-lg p-4 border border-cyan-500/20">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-white/50 text-xs mb-1">URL del Webhook</div>
                      <code className="text-cyan-300 bg-black/50 px-3 py-2 rounded font-mono text-sm block">
                        https://connect.viubux.com/api/webhook/publicala
                      </code>
                    </div>
                    <div>
                      <div className="text-white/50 text-xs mb-1">Método</div>
                      <span className="bg-green-500/20 text-green-400 px-3 py-2 rounded text-sm font-mono">
                        POST
                      </span>
                    </div>
                    <div>
                      <div className="text-white/50 text-xs mb-1">Evento</div>
                      <span className="bg-purple-500/20 text-purple-400 px-3 py-2 rounded text-sm">
                        sale (approved)
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Two Column Layout */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Synced Users */}
              <Card className="card-viubux-dark border-purple-500/20">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="gotham-bold text-white">Usuarios Sincronizados</CardTitle>
                    <CardDescription className="text-white/60">
                      Usuarios creados desde app.viubux.com
                    </CardDescription>
                  </div>
                  <Button
                    onClick={fetchPublicalaStats}
                    variant="outline"
                    size="sm"
                    className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
                    disabled={publicalaLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${publicalaLoading ? 'animate-spin' : ''}`} />
                    Actualizar
                  </Button>
                </CardHeader>
                <CardContent>
                  {publicalaLoading ? (
                    <div className="text-center py-8 text-white/50">Cargando...</div>
                  ) : publicalaSyncedUsers.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-white/20 mx-auto mb-3" />
                      <p className="text-white/50">No hay usuarios sincronizados aún</p>
                      <p className="text-white/30 text-sm mt-1">
                        Aparecerán aquí cuando lleguen compras desde app.viubux.com
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {publicalaSyncedUsers.map((u, idx) => (
                        <div key={idx} className="bg-black/30 rounded-lg p-3 border border-purple-500/10 hover:border-purple-500/30 transition-colors">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-white font-medium">{u.full_name || 'Sin nombre'}</div>
                              <div className="text-white/50 text-sm">{u.email}</div>
                            </div>
                            <div className="text-right">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                u.membership_status === 'active'
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-red-500/20 text-red-400'
                              }`}>
                                {u.membership_status === 'active' ? 'Activo' : 'Inactivo'}
                              </span>
                              {u.trial_membership && (
                                <span className="ml-2 px-2 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400">
                                  Trial
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-2 text-xs text-white/40">
                            <span>Código: <code className="text-cyan-400">{u.referral_code}</code></span>
                            <span>{new Date(u.created_at).toLocaleDateString('es-MX')}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Sync Events History */}
              <Card className="card-viubux-dark border-blue-500/20">
                <CardHeader>
                  <CardTitle className="gotham-bold text-white">Historial de Webhooks</CardTitle>
                  <CardDescription className="text-white/60">
                    Eventos de sincronización recibidos
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {publicalaLoading ? (
                    <div className="text-center py-8 text-white/50">Cargando...</div>
                  ) : publicalaEvents.length === 0 ? (
                    <div className="text-center py-8">
                      <RefreshCw className="w-12 h-12 text-white/20 mx-auto mb-3" />
                      <p className="text-white/50">No hay eventos registrados</p>
                      <p className="text-white/30 text-sm mt-1">
                        Los webhooks aparecerán aquí
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {publicalaEvents.map((e, idx) => (
                        <div key={idx} className="bg-black/30 rounded-lg p-3 border border-blue-500/10 hover:border-blue-500/30 transition-colors">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium truncate max-w-[200px]" title={e.product_name}>
                              {e.product_name || 'Producto'}
                            </span>
                            <span className="text-green-400 gotham-bold">
                              {formatCurrency(e.payment_amount_mxn || 0)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-white/50">
                            <span>{e.user_email}</span>
                            <span>{new Date(e.created_at).toLocaleString('es-MX', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}</span>
                          </div>
                          <div className="mt-2 text-xs">
                            <code className="text-blue-400/60 bg-black/30 px-2 py-0.5 rounded">
                              {e.publicala_order_uuid?.substring(0, 16)}...
                            </code>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {/* Modal: Editar Perfil del Autor (Admin) */}
      {showMergeModal && mergeSource && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="merge-account-modal">
          <Card className="card-viubux-dark border-purple-500/30 w-full max-w-lg my-8">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="gotham-bold text-white flex items-center gap-2">
                    <GitMerge className="w-5 h-5 text-teal-400" />
                    Fusionar cuenta duplicada
                  </CardTitle>
                  <CardDescription className="text-white/70">
                    Duplicada: {mergeSource.full_name} · {mergeSource.email}
                  </CardDescription>
                </div>
                <button
                  onClick={() => setShowMergeModal(false)}
                  className="text-white/50 hover:text-white"
                  data-testid="merge-account-close"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-3 text-white/80 text-sm">
                Se copiarán foto, bio, portada y enlaces faltantes a la cuenta principal, y todas las obras de la cuenta duplicada se reasignarán a la principal.
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-2">Correo de la cuenta principal (destino)</label>
                <input
                  type="email"
                  list="merge-users-datalist"
                  value={mergeTargetEmail}
                  onChange={(e) => setMergeTargetEmail(e.target.value)}
                  placeholder="correo@principal.com"
                  className="w-full bg-black/40 border border-purple-500/30 rounded-lg px-3 py-2 text-white placeholder-white/30 focus:outline-none focus:border-teal-400"
                  data-testid="merge-target-email"
                />
                <datalist id="merge-users-datalist">
                  {users.filter((x) => x.id !== mergeSource.id).map((x) => (
                    <option key={x.id} value={x.email}>{x.full_name}</option>
                  ))}
                </datalist>
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-2">¿Qué hacer con la cuenta duplicada?</label>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setMergeMode('editor')}
                    data-testid="merge-mode-editor"
                    className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${mergeMode === 'editor' ? 'border-teal-400 bg-teal-500/15' : 'border-purple-500/30 bg-black/30 hover:border-teal-500/50'}`}
                  >
                    <div className="text-white font-semibold text-sm flex items-center gap-2">
                      {mergeMode === 'editor' && <Check className="w-4 h-4 text-teal-400" />}
                      Convertir en editor/asistente (recomendado)
                    </div>
                    <div className="text-white/60 text-xs mt-1">
                      Conserva su correo y contraseña. Al iniciar sesión, administrará la cuenta principal como editor (obras, marketing, subidas). Sin acceso a datos bancarios ni pagos.
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMergeMode('deactivate')}
                    data-testid="merge-mode-deactivate"
                    className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${mergeMode === 'deactivate' ? 'border-red-400 bg-red-500/15' : 'border-purple-500/30 bg-black/30 hover:border-red-500/50'}`}
                  >
                    <div className="text-white font-semibold text-sm flex items-center gap-2">
                      {mergeMode === 'deactivate' && <Check className="w-4 h-4 text-red-400" />}
                      Solo desactivar la cuenta duplicada
                    </div>
                    <div className="text-white/60 text-xs mt-1">
                      La cuenta duplicada quedará inactiva y ya no podrá iniciar sesión.
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => setShowMergeModal(false)}
                  className="flex-1 bg-white/10 text-white hover:bg-white/20 border border-white/20"
                  data-testid="merge-cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmMerge}
                  disabled={mergeLoading}
                  className="flex-1 bg-teal-500 text-white hover:bg-teal-600"
                  data-testid="merge-confirm-btn"
                >
                  {mergeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fusionar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showEditProfileModal && profileUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="edit-profile-modal">
          <Card className="card-viubux-dark border-purple-500/30 w-full max-w-lg my-8">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="gotham-bold text-white flex items-center gap-2">
                    <Edit className="w-5 h-5 text-violet-400" />
                    Editar perfil del autor
                  </CardTitle>
                  <CardDescription className="text-white/70">
                    {profileUser.full_name} · {profileUser.email}
                  </CardDescription>
                </div>
                <button
                  onClick={() => setShowEditProfileModal(false)}
                  className="text-white/50 hover:text-white"
                  data-testid="edit-profile-close"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Avatar */}
              <div>
                <label className="block text-white/70 text-sm mb-2">Avatar (foto de perfil)</label>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-black/40 border border-purple-500/30 flex items-center justify-center shrink-0">
                    {profileForm.profile_photo
                      ? <img src={profileForm.profile_photo} alt="avatar" className="w-full h-full object-cover" />
                      : <Users className="w-6 h-6 text-white/30" />}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={profileForm.profile_photo}
                      onChange={(e) => setProfileForm({ ...profileForm, profile_photo: e.target.value })}
                      className="w-full px-3 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white text-sm"
                      placeholder="URL del avatar o sube una imagen"
                      data-testid="edit-avatar-url"
                    />
                    <label className="inline-flex items-center gap-2 text-xs text-violet-300 cursor-pointer hover:text-violet-200">
                      <ImageUp className="w-4 h-4" />
                      {uploadingField === 'profile_photo' ? 'Subiendo...' : 'Subir avatar'}
                      <input type="file" accept="image/*" className="hidden"
                        onChange={(e) => handleAdminImgUpload('profile_photo', e.target.files?.[0])}
                        data-testid="edit-avatar-file" />
                    </label>
                  </div>
                </div>
              </div>

              {/* Portada */}
              <div>
                <label className="block text-white/70 text-sm mb-2">Portada (cover / banner)</label>
                {profileForm.cover_image ? (
                  <img src={profileForm.cover_image} alt="portada" className="w-full h-28 object-cover rounded-lg border border-purple-500/30 mb-2" />
                ) : null}
                <input
                  type="text"
                  value={profileForm.cover_image}
                  onChange={(e) => setProfileForm({ ...profileForm, cover_image: e.target.value })}
                  className="w-full px-3 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white text-sm"
                  placeholder="URL de la portada o sube una imagen"
                  data-testid="edit-cover-url"
                />
                <label className="inline-flex items-center gap-2 text-xs text-violet-300 cursor-pointer hover:text-violet-200 mt-2">
                  <ImageUp className="w-4 h-4" />
                  {uploadingField === 'cover_image' ? 'Subiendo...' : 'Subir portada'}
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => handleAdminImgUpload('cover_image', e.target.files?.[0])}
                    data-testid="edit-cover-file" />
                </label>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-white/70 text-sm mb-1">Bio</label>
                <textarea
                  value={profileForm.bio}
                  onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white text-sm"
                  placeholder="Biografía del autor"
                  data-testid="edit-bio"
                />
              </div>

              {/* Primer enlace (para que su página /usuario no aparezca vacía) */}
              <div className="pt-4 border-t border-purple-500/20">
                <label className="block text-white/70 text-sm mb-1 flex items-center gap-2">
                  <LinkIcon className="w-4 h-4 text-violet-400" />
                  Primer enlace para su página pública (connect.viubux.com/usuario)
                </label>
                <p className="text-white/40 text-xs mb-2">Opcional: agrega un enlace para que su página no aparezca vacía.</p>
                <input
                  type="text"
                  value={profileForm.link_title}
                  onChange={(e) => setProfileForm({ ...profileForm, link_title: e.target.value })}
                  className="w-full px-3 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white text-sm mb-2"
                  placeholder="Título del enlace (ej. Mi libro en Viubux)"
                  data-testid="edit-link-title"
                />
                <input
                  type="text"
                  value={profileForm.link_url}
                  onChange={(e) => setProfileForm({ ...profileForm, link_url: e.target.value })}
                  className="w-full px-3 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white text-sm"
                  placeholder="https://..."
                  data-testid="edit-link-url"
                />
              </div>

              <Button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="save-profile-btn"
              >
                {savingProfile ? 'Guardando...' : 'Guardar perfil'}
              </Button>

              {/* Registrar Obra */}
              <div className="pt-4 border-t border-purple-500/20">
                <h4 className="gotham-bold text-white text-sm mb-3 flex items-center gap-2">
                  <PackageSearch className="w-4 h-4 text-amber-400" />
                  Registrar una obra a su nombre
                </h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={workForm.title}
                    onChange={(e) => setWorkForm({ ...workForm, title: e.target.value })}
                    className="w-full px-3 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white text-sm"
                    placeholder="Título de la obra *"
                    data-testid="work-title"
                  />
                  <div>
                    <p className="text-white/60 text-xs mb-2">Formatos (elige los que apliquen; cada uno con su ISBN y link de lectura):</p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {ADMIN_WORK_FORMATS.map(fmt => {
                        const active = workForm.formats.find(f => f.format === fmt.id);
                        return (
                          <button
                            key={fmt.id}
                            type="button"
                            onClick={() => toggleWorkFormat(fmt.id)}
                            className={`px-3 py-1.5 rounded-full text-xs border transition-all ${active ? 'bg-violet-500/30 border-violet-400 text-white' : 'bg-black/30 border-purple-500/30 text-white/60 hover:border-violet-400/50'}`}
                            data-testid={`work-format-${fmt.id}`}
                          >
                            {fmt.label}
                          </button>
                        );
                      })}
                    </div>
                    {workForm.formats.map(f => {
                      const label = ADMIN_WORK_FORMATS.find(x => x.id === f.format)?.label || f.format;
                      return (
                        <div key={f.format} className="bg-black/20 border border-purple-500/20 rounded-lg p-2 space-y-2 mb-2">
                          <span className="text-violet-300 text-xs font-semibold">{label}</span>
                          <input
                            type="text"
                            value={f.isbn}
                            onChange={(e) => updateWorkFormatField(f.format, 'isbn', e.target.value)}
                            className="w-full px-3 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white text-xs font-mono"
                            placeholder="ISBN (opcional, se genera VBX- si lo dejas vacío)"
                            data-testid={`work-isbn-${f.format}`}
                          />
                          <input
                            type="text"
                            value={f.url || ''}
                            onChange={(e) => updateWorkFormatField(f.format, 'url', e.target.value)}
                            className="w-full px-3 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white text-xs"
                            placeholder="Link de lectura de este formato (https://...)"
                            data-testid={`work-url-${f.format}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <input
                    type="text"
                    value={workForm.public_url}
                    onChange={(e) => setWorkForm({ ...workForm, public_url: e.target.value })}
                    className="w-full px-3 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white text-sm"
                    placeholder="URL pública / reader (opcional)"
                    data-testid="work-url"
                  />
                  <div className="flex items-center gap-3">
                    {workForm.cover_image ? (
                      <img src={workForm.cover_image} alt="cover" className="w-12 h-16 object-cover rounded border border-purple-500/30" />
                    ) : null}
                    <input
                      type="text"
                      value={workForm.cover_image}
                      onChange={(e) => setWorkForm({ ...workForm, cover_image: e.target.value })}
                      className="flex-1 px-3 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white text-sm"
                      placeholder="URL de portada de la obra"
                      data-testid="work-cover-url"
                    />
                    <label className="inline-flex items-center gap-1 text-xs text-violet-300 cursor-pointer hover:text-violet-200 shrink-0">
                      <ImageUp className="w-4 h-4" />
                      {uploadingField === 'work_cover' ? '...' : 'Subir'}
                      <input type="file" accept="image/*" className="hidden"
                        onChange={(e) => handleAdminImgUpload('work_cover', e.target.files?.[0])}
                        data-testid="work-cover-file" />
                    </label>
                  </div>
                  <Button
                    onClick={handleRegisterWork}
                    disabled={registeringWork}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="register-work-btn"
                  >
                    {registeringWork ? 'Registrando...' : 'Registrar obra'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showCreateUserModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="create-user-modal">
          <Card className="card-viubux-dark border-purple-500/30 w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="gotham-bold text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-purple-400" />
                Agregar Usuario
              </CardTitle>
              <CardDescription className="text-white/70">
                Crear un nuevo usuario sin pasar por el registro normal
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-1">Nombre Completo</label>
                <input
                  type="text"
                  value={newUserData.full_name}
                  onChange={(e) => setNewUserData({...newUserData, full_name: e.target.value})}
                  className="w-full px-4 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white"
                  placeholder="Juan Pérez"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Email</label>
                <input
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                  className="w-full px-4 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white"
                  placeholder="juan@email.com"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Nombre de Usuario</label>
                <input
                  type="text"
                  value={newUserData.username}
                  onChange={(e) => setNewUserData({...newUserData, username: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '')})}
                  className="w-full px-4 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white"
                  placeholder="juanperez"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Contraseña</label>
                <input
                  type="password"
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                  className="w-full px-4 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Tipo de Usuario</label>
                <select
                  value={newUserData.user_type}
                  onChange={(e) => setNewUserData({...newUserData, user_type: e.target.value})}
                  className="w-full px-4 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white"
                >
                  <option value="consumer">Audio-Lector</option>
                  <option value="author">Autor</option>
                  <option value="editorial">Editorial / Publisher</option>
                </select>
              </div>
              {newUserData.user_type === 'author' && (
                <div data-testid="new-user-tier-wrap">
                  <label className="block text-white/70 text-sm mb-1">Tipo de Autor</label>
                  <select
                    data-testid="new-user-tier-select"
                    value={newUserData.author_tier || 'premium'}
                    onChange={(e) => setNewUserData({...newUserData, author_tier: e.target.value})}
                    className="w-full px-4 py-2 bg-black/30 border border-purple-500/30 rounded-lg text-white"
                  >
                    <option value="premium">Premium (acceso completo)</option>
                    <option value="lite">Lite (solo regalías)</option>
                  </select>
                </div>
              )}
              {newUserData.user_type === 'editorial' && (
                <div data-testid="new-user-editorial-tier-wrap">
                  <label className="block text-white/70 text-sm mb-1">Tipo de Editorial</label>
                  <select
                    data-testid="new-user-editorial-tier-select"
                    value={newUserData.editorial_tier || 'lite'}
                    onChange={(e) => setNewUserData({...newUserData, editorial_tier: e.target.value})}
                    className="w-full px-4 py-2 bg-black/30 border border-amber-500/30 rounded-lg text-white"
                  >
                    <option value="lite">Lite (solo ventas directas de sus obras)</option>
                    <option value="premium">Premium (Pool + desglose por tienda + split autor)</option>
                  </select>
                </div>
              )}
              <div>
                <label className="flex items-center gap-2 text-white/70 text-sm">
                  <input
                    type="checkbox"
                    data-testid="new-user-activate"
                    checked={newUserData.activate || false}
                    onChange={(e) => setNewUserData({...newUserData, activate: e.target.checked})}
                    className="accent-purple-500"
                  />
                  Activar membresía de inmediato (sin pago)
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateUserModal(false)}
                  className="flex-1 border-white/30 text-white hover:bg-white/10"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreateUser}
                  disabled={creatingUser}
                  className="flex-1 btn-viubux"
                >
                  {creatingUser ? 'Creando...' : 'Crear Usuario'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View As User Modal */}
      {viewAsUserId && (
        <ViewAsUserModal
          token={token}
          userId={viewAsUserId}
          onClose={() => setViewAsUserId(null)}
        />
      )}

      {/* Bank Transfer Proof Detail Modal */}
      {showProofModal && selectedProof && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="gotham-bold text-xl text-white">Detalle del Comprobante</h2>
              <button
                onClick={() => {
                  setShowProofModal(false);
                  setSelectedProof(null);
                }}
                className="text-white/60 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="bg-black/30 rounded-xl p-4">
                <h3 className="text-white/60 text-sm mb-3">Información del Usuario</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-white/50 text-xs">Nombre</p>
                    <p className="text-white font-medium">{selectedProof.user_name}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Email</p>
                    <p className="text-white">{selectedProof.user_email}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Tipo</p>
                    <p className="text-white">{selectedProof.user_type === 'author' ? 'Autor' : 'Audio-Lector'}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Plan</p>
                    <p className="text-white">{selectedProof.plan_type}</p>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
                <p className="text-white/60 text-sm">Monto Declarado</p>
                <p className="text-3xl font-bold text-green-400 mt-1">
                  ${selectedProof.amount?.toLocaleString()} MXN
                </p>
              </div>

              {/* Proof Image */}
              <div>
                <h3 className="text-white/60 text-sm mb-3">Comprobante Adjunto</h3>
                <div className="bg-black/30 rounded-xl p-4">
                  {selectedProof.proof_content_type?.startsWith('image/') ? (
                    <img
                      src={`data:${selectedProof.proof_content_type};base64,${selectedProof.proof_file_b64}`}
                      alt="Comprobante"
                      className="max-w-full rounded-lg mx-auto"
                      style={{ maxHeight: '400px' }}
                    />
                  ) : (
                    <div className="text-center py-8">
                      <FileImage className="w-16 h-16 text-white/40 mx-auto mb-3" />
                      <p className="text-white/60">Archivo PDF</p>
                      <p className="text-white/40 text-sm">{selectedProof.proof_filename}</p>
                      <Button
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = `data:${selectedProof.proof_content_type};base64,${selectedProof.proof_file_b64}`;
                          link.download = selectedProof.proof_filename;
                          link.click();
                        }}
                        className="mt-4 bg-white/10 text-white hover:bg-white/20"
                      >
                        Descargar PDF
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between bg-black/30 rounded-xl p-4">
                <div>
                  <p className="text-white/50 text-xs">Estado Actual</p>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedProof.status === 'pending_verification' 
                      ? 'bg-orange-500/20 text-orange-400' 
                      : selectedProof.status === 'approved'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {selectedProof.status === 'pending_verification' ? 'Pendiente de Verificación' : 
                     selectedProof.status === 'approved' ? 'Aprobado' : 'Rechazado'}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-white/50 text-xs">Fecha de Envío</p>
                  <p className="text-white text-sm">
                    {new Date(selectedProof.created_at).toLocaleString('es-MX')}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              {selectedProof.status === 'pending_verification' && (
                <div className="flex gap-4">
                  <Button
                    onClick={() => handleVerifyProof(selectedProof.id, false)}
                    disabled={processingProof}
                    className="flex-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/50"
                  >
                    <XCircle className="w-5 h-5 mr-2" />
                    Rechazar
                  </Button>
                  <Button
                    onClick={() => handleVerifyProof(selectedProof.id, true)}
                    disabled={processingProof}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    {processingProof ? (
                      <>
                        <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Aprobar y Activar Membresía
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Actualizar Transacción */}
      {showUpdateTxModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="update-tx-modal">
          <div className="bg-gray-900 border border-blue-500/30 rounded-2xl max-w-md w-full p-6">
            <h3 className="gotham-bold text-xl text-white mb-4 flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-400" />
              Actualizar Transacción
            </h3>
            
            {/* Info de la transacción */}
            <div className="bg-black/30 rounded-lg p-4 mb-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/60">Usuario:</span>
                <span className="text-white">{selectedTransaction.user_name || selectedTransaction.user_email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Monto:</span>
                <span className="text-white font-bold">${selectedTransaction.amount} {selectedTransaction.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Estado actual:</span>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  selectedTransaction.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {selectedTransaction.payment_status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Proveedor:</span>
                <span className="text-white capitalize">{selectedTransaction.payment_provider}</span>
              </div>
            </div>

            {/* Formulario */}
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Nuevo Estado</label>
                <select
                  value={newTxStatus}
                  onChange={(e) => setNewTxStatus(e.target.value)}
                  className="w-full px-3 py-2 bg-black/50 border border-white/20 rounded-lg text-white"
                >
                  <option value="paid">✅ Paid (Pagado)</option>
                  <option value="pending">⏳ Pending (Pendiente)</option>
                  <option value="failed">❌ Failed (Fallido)</option>
                  <option value="refunded">↩️ Refunded (Reembolsado)</option>
                  <option value="cancelled">🚫 Cancelled (Cancelado)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-white/70 text-sm mb-2">ID Pago MercadoPago (opcional)</label>
                <input
                  type="text"
                  value={txMpPaymentId}
                  onChange={(e) => setTxMpPaymentId(e.target.value)}
                  placeholder="Ej: 154793000700"
                  className="w-full px-3 py-2 bg-black/50 border border-white/20 rounded-lg text-white placeholder-white/40"
                />
              </div>
              
              <div>
                <label className="block text-white/70 text-sm mb-2">Notas (opcional)</label>
                <input
                  type="text"
                  value={txNotes}
                  onChange={(e) => setTxNotes(e.target.value)}
                  placeholder="Razón de la actualización..."
                  className="w-full px-3 py-2 bg-black/50 border border-white/20 rounded-lg text-white placeholder-white/40"
                />
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => {
                  setShowUpdateTxModal(false);
                  setSelectedTransaction(null);
                }}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUpdateTransactionStatus}
                disabled={updatingTx}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
              >
                {updatingTx ? 'Actualizando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Diagnóstico BD */}
      {showDbDiagnostic && dbDiagnostic && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="db-diagnostic-modal">
          <div className="bg-gray-900 border border-green-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="gotham-bold text-xl text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Diagnóstico de Base de Datos
              </h3>
              <Button
                onClick={() => setShowDbDiagnostic(false)}
                variant="ghost"
                size="sm"
                className="text-white/50 hover:text-white"
              >
                ✕
              </Button>
            </div>
            
            {/* Status */}
            <div className={`mb-4 p-3 rounded-lg ${dbDiagnostic.status === 'connected' ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
              <p className={`font-bold ${dbDiagnostic.status === 'connected' ? 'text-green-400' : 'text-red-400'}`}>
                Estado: {dbDiagnostic.status === 'connected' ? '✅ Conectado' : '❌ Error'}
              </p>
              <p className="text-white/70 text-sm">{dbDiagnostic.message}</p>
              {dbDiagnostic.database_name && <p className="text-white/50 text-xs mt-1">BD: {dbDiagnostic.database_name}</p>}
            </div>

            {/* User Stats */}
            {dbDiagnostic.user_stats && (
              <div className="mb-4">
                <h4 className="text-white font-medium mb-2">Estadísticas de Usuarios</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{dbDiagnostic.user_stats.total}</p>
                    <p className="text-white/50 text-xs">Total</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-400">{dbDiagnostic.user_stats.active}</p>
                    <p className="text-white/50 text-xs">Activos</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-400">{dbDiagnostic.user_stats.pending}</p>
                    <p className="text-white/50 text-xs">Pendientes</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-purple-400">{dbDiagnostic.user_stats.authors}</p>
                    <p className="text-white/50 text-xs">Autores</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-blue-400">{dbDiagnostic.user_stats.consumers}</p>
                    <p className="text-white/50 text-xs">Audio-lectores</p>
                  </div>
                </div>
              </div>
            )}

            {/* Transaction Stats */}
            {dbDiagnostic.transaction_stats && (
              <div className="mb-4">
                <h4 className="text-white font-medium mb-2">Transacciones</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-white">{dbDiagnostic.transaction_stats.total}</p>
                    <p className="text-white/50 text-xs">Total</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-green-400">{dbDiagnostic.transaction_stats.paid}</p>
                    <p className="text-white/50 text-xs">Pagadas</p>
                  </div>
                </div>
              </div>
            )}

            {/* Collections */}
            {dbDiagnostic.collections && Object.keys(dbDiagnostic.collections).length > 0 && (
              <div className="mb-4">
                <h4 className="text-white font-medium mb-2">Colecciones</h4>
                <div className="bg-black/30 rounded-lg p-3 space-y-1 max-h-40 overflow-y-auto">
                  {Object.entries(dbDiagnostic.collections).map(([name, count]) => (
                    <div key={name} className="flex justify-between text-sm">
                      <span className="text-white/70">{name}</span>
                      <span className="text-white font-mono">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Users */}
            {dbDiagnostic.recent_users && dbDiagnostic.recent_users.length > 0 && (
              <div>
                <h4 className="text-white font-medium mb-2">Últimos 5 Usuarios</h4>
                <div className="bg-black/30 rounded-lg p-3 space-y-2">
                  {dbDiagnostic.recent_users.map((u, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-white/70">{u.email}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${u.membership_status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                        {u.membership_status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {dbDiagnostic.error && (
              <div className="mt-4 bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-400 text-sm font-mono">{dbDiagnostic.error}</p>
                {dbDiagnostic.traceback && (
                  <pre className="text-red-300 text-xs mt-2 overflow-x-auto">{dbDiagnostic.traceback}</pre>
                )}
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <Button
                onClick={() => setShowDbDiagnostic(false)}
                className="bg-white/10 hover:bg-white/20 text-white"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Distribute Yearly Bonus Confirmation Modal */}
      {showDistributeModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-yellow-500/50 rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-yellow-500/30">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Award className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <h2 className="gotham-bold text-xl text-yellow-400">Distribuir Bono Anual</h2>
                  <p className="text-white/60 text-sm">Esta acción no se puede deshacer</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Summary */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-white/50 text-xs">Pool a Distribuir</div>
                    <div className="gotham-bold text-xl text-yellow-400">
                      {formatCurrency(yearlyBonusPreview?.yearly_bonus_pool || 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-white/50 text-xs">Beneficiarios</div>
                    <div className="gotham-bold text-xl text-yellow-400">
                      {yearlyBonusPreview?.top_20_count || 0} autores
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                <p className="text-blue-300 text-sm flex items-start gap-2">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Al confirmar, se distribuirá el bono entre los top 20 autores y se actualizará su balance disponible.
                    Cada autor recibirá una notificación.
                  </span>
                </p>
              </div>

              <p className="text-white/70 text-sm">
                Para confirmar, escribe exactamente:
              </p>
              
              <div className="bg-black/30 rounded-lg p-3 text-center">
                <code className="text-yellow-400 gotham-bold">DISTRIBUTE BONUS {new Date().getFullYear()}</code>
              </div>

              <input
                type="text"
                value={distributeConfirmation}
                onChange={(e) => setDistributeConfirmation(e.target.value.toUpperCase())}
                className="w-full bg-black/30 border border-yellow-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-yellow-500 font-mono text-center"
                placeholder="Escribe aquí..."
                data-testid="distribute-confirmation-input"
              />
            </div>
            
            <div className="p-6 border-t border-yellow-500/30 flex gap-3">
              <Button
                onClick={() => {
                  setShowDistributeModal(false);
                  setDistributeConfirmation('');
                }}
                variant="outline"
                className="flex-1 border-white/20 text-white/70 hover:bg-white/10"
                disabled={distributing}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDistributeYearlyBonus}
                disabled={distributing || distributeConfirmation !== `DISTRIBUTE BONUS ${new Date().getFullYear()}`}
                className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-black gotham-bold disabled:opacity-50"
                data-testid="confirm-distribute-btn"
              >
                {distributing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Distribuyendo...
                  </>
                ) : (
                  <>
                    <DollarSign className="w-4 h-4 mr-2" />
                    Distribuir Bono
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;